import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import { List } from 'react-window';
// Phase 3: Using state-based overlay instead of routing
import { api } from '../api/client';
import { toast, Toaster } from 'sonner';
import { debounce } from 'lodash';

// Phase 2: Pricing Updates Hook
import { usePricingUpdates } from '../hooks/usePricingUpdates';

// UI Components
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Label } from './ui/label';
import { Tabs } from './ui/tabs';

// Phase 2: Task 2 - Recalculate Pricing Components
import RecalculatePricingModal from './ui/RecalculatePricingModal';
import ProgressOverlay from './ui/ProgressOverlay';
import PricingDrawer from './ui/PricingDrawer';
import UnsavedChangesModal from './ui/UnsavedChangesModal';

// Bulk Product Upload Component
import BulkProductUpload from './BulkProductUpload';

// Product Components
import SupplierSkuManager from './product/SupplierSkuManager';
import BOMManager from './product/BOMManager';

// Column Catalog and Layout Variants
import { PRODUCT_COLUMN_CATALOG, getColumnById, filterColumnsByRBAC, formatters } from '../product/columns/catalog';
import { useLayoutVariants } from '../hooks/useLayoutVariants';
import ViewSettingsModal from './ViewSettingsModal';
import RowActions from './RowActions';

// Phase 3: Enhanced RBAC
import { 
  getUserRoles, 
  getDemoUserRole, 
  canEditField, 
  getFieldCategory, 
  getFieldAccessLevel,
  getRestrictedFieldMessage,
  ROLES 
} from '../utils/rbac';

// Phase 3: Product Creation and Detail
import ProductCreateWizard from './ProductCreateWizard';
import ProductDetailPage from './ProductDetailPage';
import ProductMasterDetail from './ProductMasterDetail';
import ProductTypeTab from './ProductTypeTab';

// Competition Tab
import CompetitionTab from './product/CompetitionTab';

// Pricing Tab - Full featured with fulfillment inputs and COGS per channel
import PricingTab from './product/PricingTab';

// Icons
import {
  Search, Filter, Plus, Settings, MoreVertical, Eye, Edit,
  ArrowUpDown, ArrowUp, ArrowDown, Copy, Archive, Trash2,
  Grid3X3, BarChart3, Package, DollarSign, LayoutGrid,
  Minimize2,
  Maximize2,
  Clock, CheckCircle, AlertTriangle, ArrowLeft, Save, RefreshCw, Upload, Download,
  Trophy,
} from 'lucide-react';

// Move API constant outside component to prevent recreation
const MOCK_TENANT_ID = 'tenant-123';

// Smart Grid Stability Patch - Fixed row height and column widths
const ROW_HEIGHT = 32; // Must match CSS .zg-grid height - compact

// Column width definitions (SAP-parity) - moved inside component for safety
const DEFAULT_COLUMN_WIDTHS = {
  sku: 96,
  product_name: 320,  // frozen
  category: 180,
  status: 120,
  reorder_point: 130,
  target_stock: 130,
  qty_on_hand: 120,
  effective_cost: 140,
  selling_price: 140,
  profit_margin: 140,
  actions: 56
};

const SmartGrid = ({ zenMode: zenModeProp, setZenMode: setZenModeProp, navCollapsed, setNavCollapsed }) => {
  // Internal zenMode state (used when no props provided)
  const [internalZenMode, setInternalZenMode] = useState(false);
  
  // Use props if provided, otherwise use internal state
  const zenMode = zenModeProp !== undefined ? zenModeProp : internalZenMode;
  const setZenMode = setZenModeProp !== undefined ? setZenModeProp : setInternalZenMode;
  // Smart Grid Stability Patch - Ultra-defensive approach to prevent Object.values errors
  const COLUMN_WIDTHS = {
    sku: 96,
    product_name: 320,
    category: 180,
    status: 120,
    reorder_point: 130,
    target_stock: 130,
    qty_on_hand: 120,
    effective_cost: 140,
    selling_price: 140,
    profit_margin: 140,
    actions: 56
  };
  
  // Calculate total width for expanded column set including Phase 1 pricing columns
  // Base columns: 96 + 320 + 180 + 120 = 716px (checkbox + SKU + name + category + status)
  // Phase 1 pricing: 120 + 140 + 130 + 130 + 140 + 100 = 760px (6 pricing columns)
  // Remaining columns: ~2000px (remaining original + expanded columns)  
  // Total: 716 + 760 + 2000 = 3476px
  const TOTAL_GRID_WIDTH = 3476; // Wide enough to trigger horizontal scrolling with pricing columns

  // State management
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);  // Start as true to show spinner until data loads
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [grouping, setGrouping] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [selectedRows, setSelectedRows] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [showColumnManager, setShowColumnManager] = useState(false);
  const [effectiveCosts, setEffectiveCosts] = useState({});
  
  // 🔍 CRITICAL DEBUG: Watch data changes and log structure
  useEffect(() => {
    if (data && data.length > 0) {
      console.log('═══════════════════════════════════════════════════════════════');
      console.log('🔍 DATA UPDATED - First Row Raw Data:', data[0]);
      console.log('🔍 SKU:', data[0].sku);
      console.log('🔍 inventory object:', data[0].inventory);
      console.log('🔍 inventory.current_stock:', data[0].inventory?.current_stock);
      console.log('🔍 current_stock (top-level):', data[0].current_stock);
      console.log('🔍 planning object:', data[0].planning);
      console.log('🔍 planning.on_hand:', data[0].planning?.on_hand);
      console.log('🔍 on_hand (top-level):', data[0].on_hand);
      console.log('═══════════════════════════════════════════════════════════════');
    }
  }, [data]);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  
  // Phase 2: Pricing Updates Hook (Phase B: Added wholesale/retail handlers)
  const { 
    updateProductPricing, 
    updateWholesaleMargin, 
    updateRetailMargin, 
    isUpdating, 
    error: pricingError 
  } = usePricingUpdates();

  // Phase D: Pricing Drawer State
  const [isPricingDrawerOpen, setIsPricingDrawerOpen] = useState(false);
  const [selectedProductForPricing, setSelectedProductForPricing] = useState(null);
  
  // Phase 2: Column Resizing State
  const [columnSizing, setColumnSizing] = useState({});
  const [isResizing, setIsResizing] = useState(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);
  
  // Phase 2: Tooltip State
  const [hoveredCell, setHoveredCell] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  
  // Quick View/Editor Drawer State
  const [showQuickView, setShowQuickView] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeDrawerTab, setActiveDrawerTab] = useState('general');
  const [drawerData, setDrawerData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  
  // Unsaved Changes Modal State for Pricing Tab
  const [pricingTabDirty, setPricingTabDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null); // {type: 'tab'|'close', target: value}
  const [pricingTabKey, setPricingTabKey] = useState(0); // Key to force PricingTab remount
  const pricingTabSaveRef = useRef(null);
  
  // Phase 3: Full-page detail overlay state
  const [showFullPageDetail, setShowFullPageDetail] = useState(false);
  const [fullPageProductId, setFullPageProductId] = useState(null);
  const [initialDetailTab, setInitialDetailTab] = useState('general');
  
  // Phase 3: RBAC state
  const [currentUserRoles, setCurrentUserRoles] = useState([ROLES.ADMIN]);
  
  // Phase 2: Task 2 - Recalculate Pricing State
  const [showRecalculateModal, setShowRecalculateModal] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [recalculateProgress, setRecalculateProgress] = useState('');

  // Bulk Upload State
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  
  // Product Wizard State
  const [showProductWizard, setShowProductWizard] = useState(false);
  
  // Delete Confirmation State
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Demo role switcher for testing
  const switchRole = useCallback((role) => {
    setCurrentUserRoles([role]);
    localStorage.setItem('demo_user_role', role);
    toast.success(`Switched to ${role.toUpperCase()} role`);
  }, []);
  
  // Full-Page Detail View State (removed duplicate)
  
  // Layout Variants State  
  const [showViewSettings, setShowViewSettings] = useState(false);
  const [showColumnPanel, setShowColumnPanel] = useState(false); // Column customization panel
  const [customColumns, setCustomColumns] = useState(null); // Track custom column selection
  const {
    variants,
    currentVariant,
    loading: variantsLoading,
    applyVariant,
    saveVariant,
    deleteVariant,
    duplicateVariant,
    setDefaultVariant,
    saveEphemeralChanges
  } = useLayoutVariants();
  
  // Get preset variants only (All, Pricing, Inventory, Compact)
  const presetVariants = variants.filter(v => v.isPreset);

  // Handle cell save - moved here to be accessible by columns
  const handleCellSave = useCallback(async (id, field, value) => {
    try {
      await api.patch(`/api/v1/product-master/item/${id}?tenant_id=${MOCK_TENANT_ID}`, {
        [field]: value
      });
      
      // Update local data
      setData(prev => prev.map(item => 
        item.id === id ? { ...item, [field]: value } : item
      ));
      
      toast.success('Updated successfully');
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error('Failed to update');
    }
  }, []);

  // Phase 2: Handle Pricing Updates with optimistic UI updates
  const handlePricingUpdate = useCallback(async (sku, updates) => {
    try {
      console.log(`Updating pricing for ${sku}:`, updates);
      
      // Optimistic update - update local data immediately
      const optimisticUpdateFn = (currentSku, updateData) => {
        setData(prev => prev.map(item => 
          item.sku === currentSku ? { 
            ...item, 
            ...updateData,
            // Recalculate net margin if final price or costs changed
            net_margin_percent: updateData.final_price && item.total_cost ? 
              (((Number(updateData.final_price) || 0) - (Number(item.total_cost) || 0)) / (Number(updateData.final_price) || 1) * 100).toFixed(2) : 
              item.net_margin_percent
          } : item
        ));
      };

      // Call API with optimistic update
      const result = await updateProductPricing(sku, updates, {
        optimisticUpdate: true,
        onOptimisticUpdate: optimisticUpdateFn
      });

      console.log('Pricing update successful:', result);

      // Refresh the affected row with real data from API  
      setData(prev => prev.map(item => 
        item.sku === sku ? { 
          ...item, 
          target_margin_percent: result.data.pricing.target_margin_percent,
          system_price: result.data.pricing.system_price,
          final_price: result.data.pricing.final_price,
          net_margin_percent: result.data.pricing.net_margin_percent,
          pricing_mode: result.data.pricing.pricing_mode,
          total_cost: result.data.cost_breakdown.total_cost
        } : item
      ));

    } catch (error) {
      console.error(`Failed to update pricing for ${sku}:`, error);
      
      // Revert optimistic update on error - we'll handle this in the UI component
      console.warn('Optimistic update should be reverted due to error');
      
      throw error; // Let EditableCell handle the error display
    }
  }, [updateProductPricing]);

  // Phase B: Wholesale Margin Update Handler
  const handleWholesaleMarginUpdate = useCallback(async (sku, newMargin) => {
    try {
      console.log(`Updating wholesale margin for ${sku}:`, newMargin);
      
      const optimisticUpdateFn = (currentSku, updateData) => {
        setData(prev => prev.map(item => 
          item.sku === currentSku ? { ...item, ...updateData } : item
        ));
      };

      const revertUpdateFn = (currentSku) => {
        // Revert to previous state - refresh data to get original values
        console.log(`Reverting wholesale margin update for ${currentSku}`);
        // Note: In production, we'd store the previous state instead of refreshing
        // For now, we'll rely on the error handling in the usePricingUpdates hook
      };

      await updateWholesaleMargin(sku, newMargin, {
        onOptimisticUpdate: optimisticUpdateFn,
        onRevertUpdate: revertUpdateFn
      });

      // Note: Data will be refreshed automatically by the API response
      console.log(`Wholesale margin update completed for ${sku}`);
      
    } catch (error) {
      console.error('Wholesale margin update error:', error);
    }
  }, [updateWholesaleMargin]);

  // Phase B: Retail Margin Update Handler  
  const handleRetailMarginUpdate = useCallback(async (sku, newMargin) => {
    try {
      console.log(`Updating retail margin for ${sku}:`, newMargin);
      
      const optimisticUpdateFn = (currentSku, updateData) => {
        setData(prev => prev.map(item => 
          item.sku === currentSku ? { ...item, ...updateData } : item
        ));
      };

      const revertUpdateFn = (currentSku) => {
        // Revert to previous state - refresh data to get original values
        console.log(`Reverting retail margin update for ${currentSku}`);
        // Note: In production, we'd store the previous state instead of refreshing
        // For now, we'll rely on the error handling in the usePricingUpdates hook
      };

      await updateRetailMargin(sku, newMargin, {
        onOptimisticUpdate: optimisticUpdateFn,
        onRevertUpdate: revertUpdateFn
      });

      // Note: Data will be refreshed automatically by the API response
      console.log(`Retail margin update completed for ${sku}`);
      
    } catch (error) {
      console.error('Retail margin update error:', error);
    }
  }, [updateRetailMargin]);

  // Phase D: Pricing Drawer Handlers
  const handleOpenPricingDrawer = useCallback(async (sku) => {
    try {
      // Find the product in current data
      const product = data.find(item => item.sku === sku);
      if (product) {
        setSelectedProductForPricing(product);
        setIsPricingDrawerOpen(true);
      } else {
        console.warn(`Product not found for SKU: ${sku}`);
      }
    } catch (error) {
      console.error('Error opening pricing drawer:', error);
      toast.error('Failed to open pricing drawer');
    }
  }, [data]);

  const handlePricingDrawerSave = useCallback(async (sku, updatedData) => {
    try {
      // Update the grid with new pricing data
      setData(prev => prev.map(item => 
        item.sku === sku 
          ? { 
              ...item, 
              ...updatedData.pricing,
              manufacturing_cost: updatedData.cost_breakdown?.manufacturing_cost || item.manufacturing_cost,
              total_cost: updatedData.cost_breakdown?.total_cost || item.total_cost
            } 
          : item
      ));
      
      console.log(`Pricing updated for ${sku}:`, updatedData);
    } catch (error) {
      console.error('Error saving pricing from drawer:', error);
    }
  }, []);

  const handleClosePricingDrawer = useCallback(() => {
    setIsPricingDrawerOpen(false);
    setSelectedProductForPricing(null);
  }, []);

  // Phase 2: Permission checker for inline editing
  const hasEditPermission = useCallback((...permissions) => {
    return permissions.some(permission => {
      if (permission === 'finance.edit') {
        return currentUserRoles.includes('Finance') || currentUserRoles.includes('Admin');
      }
      if (permission === 'ownerops.edit') {
        return currentUserRoles.includes('OwnerOps') || currentUserRoles.includes('Admin');
      }
      return false;
    });
  }, [currentUserRoles]);

  // Handle Quick View drawer - moved here to be accessible by columns
  const handleQuickView = useCallback(async (product) => {
    // ✅ Safety check: Validate product data
    if (!product) {
      console.error('❌ handleQuickView: product is undefined');
      toast.error('Cannot open product: Invalid product data');
      return;
    }
    if (!product.id) {
      console.error('❌ handleQuickView: product.id is missing', product);
      toast.error('Cannot open product: Missing product ID');
      return;
    }
    
    try {
      setSelectedProduct(product);
      setActiveDrawerTab('general');
      
      // Reset save status when opening a new product
      setHasUnsavedChanges(false);
      setPricingTabDirty(false); // Reset pricing dirty state
      setIsSaving(false);
      setLastSaved(null);
      pendingUpdates.current = {};
      // Cancel any pending debounced save
      debouncedSave.current?.cancel?.();
      
      // Fetch detailed product data for the drawer
      const data = await api.get(`/api/v1/product-master/item/${product.id}/details?tenant_id=tenant-123`);
      setDrawerData(data || {});
      
      setShowQuickView(true);
    } catch (error) {
      console.error('Error loading product details:', error);
      toast.error('Failed to load product details');
    }
  }, []);
  
  // ============================================================
  // UNSAVED CHANGES MODAL HANDLERS FOR DRAWER TABS
  // ============================================================
  
  // Handle drawer tab switch with unsaved changes check
  const handleDrawerTabSwitch = useCallback((newTab) => {
    console.log('🔄🔄🔄 handleDrawerTabSwitch CALLED 🔄🔄🔄');
    console.log('🔄 Parameters:', { newTab, activeDrawerTab, pricingTabDirty });
    
    // Check if current tab is pricing and has unsaved changes
    if (pricingTabDirty && activeDrawerTab === 'pricing' && newTab !== 'pricing') {
      console.log('🛑🛑🛑 INTERCEPTED - blocking tab switch 🛑🛑🛑');
      setPendingNavigation({ type: 'tab', target: newTab });
      setShowUnsavedModal(true);
      return; // BLOCK the switch
    }
    
    console.log('✅ Allowing tab switch to:', newTab);
    setActiveDrawerTab(newTab);
  }, [activeDrawerTab, pricingTabDirty]);
  
  // Handle drawer close with unsaved changes check
  const handleDrawerClose = useCallback(() => {
    console.log('🔙 handleDrawerClose called:', { activeDrawerTab, pricingTabDirty });
    
    if (pricingTabDirty && activeDrawerTab === 'pricing') {
      console.log('🛑 BLOCKING CLOSE - showing modal');
      setPendingNavigation({ type: 'close' });
      setShowUnsavedModal(true);
      return;
    }
    
    setShowQuickView(false);
  }, [activeDrawerTab, pricingTabDirty]);
  
  // Handle "Leave anyway" from modal
  const handleDiscardChanges = useCallback(() => {
    console.log('🗑️ handleDiscardChanges CALLED');
    console.log('🗑️ pendingNavigation:', pendingNavigation);
    
    // Store the navigation target BEFORE clearing state
    const navType = pendingNavigation?.type;
    const navTarget = pendingNavigation?.target;
    
    // Clear dirty state and modal
    setPricingTabDirty(false);
    setShowUnsavedModal(false);
    setPendingNavigation(null);
    
    // Now execute the navigation
    if (navType === 'tab') {
      console.log('🗑️ Switching to tab:', navTarget);
      setActiveDrawerTab(navTarget);
    } else if (navType === 'close') {
      console.log('🗑️ Closing drawer');
      setShowQuickView(false);
    }
  }, [pendingNavigation]);
  
  // Handle "Stay & Save" from modal
  const handleStayAndSave = useCallback(async () => {
    console.log('💾 handleStayAndSave CALLED');
    setShowUnsavedModal(false);
    
    // Try to trigger save on PricingTab via ref
    if (pricingTabSaveRef.current?.triggerSave) {
      try {
        setIsSaving(true);
        console.log('💾 Calling triggerSave...');
        await pricingTabSaveRef.current.triggerSave();
        console.log('💾 triggerSave completed successfully');
        setPricingTabDirty(false);
        
        // Bug 3 Fix: Show success toast IMMEDIATELY after save
        console.log('🎉 Showing success toast');
        toast.success('Pricing saved successfully');
        
        // Bug 2 Fix: Re-fetch product data AND force PricingTab remount
        if (selectedProduct?.id) {
          console.log('🔄 Re-fetching product data...');
          try {
            // Use /details endpoint for full product data
            const freshData = await api.get(`/api/v1/product-master/item/${selectedProduct.id}/details?tenant_id=tenant-123`);
            setDrawerData(freshData || {});
            // Update selectedProduct with fresh data from the same endpoint
            if (freshData) {
              setSelectedProduct(prev => ({ ...prev, ...freshData }));
            }
            // Force PricingTab remount by changing its key
            setPricingTabKey(prev => prev + 1);
            console.log('✅ Product data refreshed, PricingTab will remount');
          } catch (refreshError) {
            console.warn('Failed to refresh product data:', refreshError);
          }
        }
        
        // After successful save, proceed with pending navigation
        if (pendingNavigation?.type === 'tab') {
          console.log('➡️ Navigating to tab:', pendingNavigation.target);
          setActiveDrawerTab(pendingNavigation.target);
        } else if (pendingNavigation?.type === 'close') {
          console.log('🚪 Closing drawer');
          setShowQuickView(false);
        }
        setPendingNavigation(null);
      } catch (error) {
        console.error('❌ Failed to save:', error);
        toast.error('Failed to save changes. Please try again.');
      } finally {
        setIsSaving(false);
      }
    } else {
      console.warn('⚠️ pricingTabSaveRef.current?.triggerSave not available');
      toast.info('Please click outside the input field to save your changes');
      setPendingNavigation(null);
    }
  }, [pendingNavigation, selectedProduct]);
  
  // Callback for PricingTab dirty state changes
  const handlePricingDirtyChange = useCallback((isDirty) => {
    console.log('📥 SmartGrid received pricingTabDirty:', isDirty);
    setPricingTabDirty(isDirty);
  }, []);
  
  // Callback for PricingTab save complete
  const handlePricingSaveComplete = useCallback(() => {
    setPricingTabDirty(false);
  }, []);

  // Actual save function (called by debounced version)
  const saveToBackend = useCallback(async (productId, updates) => {
    if (!productId) return;
    
    setIsSaving(true);
    try {
      await api.patch(`/api/v1/product-master/item/${productId}?tenant_id=${MOCK_TENANT_ID}`, updates);
      
      // Update local data
      setData(prev => prev.map(item => 
        item.id === productId ? { ...item, ...updates } : item
      ));
      
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
      
      // Only show success toast for manual saves to avoid spam
      if (Object.keys(updates).length > 1) {
        toast.success('All changes saved successfully');
      }
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  }, []);

  // Store pending updates for debounced save
  const pendingUpdates = useRef({});
  
  // Debounced save function - waits 1 second after user stops typing
  const debouncedSave = useRef(null);
  
  // Initialize debounced save function
  useEffect(() => {
    debouncedSave.current = debounce(async (productId, pendingUpdates) => {
      await saveToBackend(productId, pendingUpdates);
    }, 1000); // 1 second delay
    
    // Cleanup on unmount
    return () => {
      debouncedSave.current?.cancel?.();
    };
  }, [saveToBackend]);

  // Handle drawer data change (NO AUTO-SAVE - Manual save only)
  const handleDrawerSave = useCallback((field, value) => {
    if (!selectedProduct) return;
    
    console.log('🟢 Field changed:', field, '=', value); // This should log immediately
    
    // Update UI immediately for responsive typing (no lag!)
    setSelectedProduct(prev => ({ ...prev, [field]: value }));
    setDrawerData(prev => ({ ...prev, [field]: value }));
    
    // Mark as having unsaved changes
    setHasUnsavedChanges(true);
    
    // Store the update in pending changes
    pendingUpdates.current = {
      ...pendingUpdates.current,
      [field]: value
    };
    
    // ❌ REMOVED: NO AUTO-SAVE - User must click "Save All" button
    // debouncedSave.current?.(selectedProduct.id, { ...pendingUpdates.current });
  }, [selectedProduct]);

  // Manual save function for "Save All" button
  const handleManualSave = useCallback(async () => {
    if (!selectedProduct || Object.keys(pendingUpdates.current).length === 0) return;
    
    console.log('🔵 Saving product...', pendingUpdates.current); // This should only appear when clicking Save All
    
    // Cancel any pending debounced save
    debouncedSave.current?.cancel?.();
    
    // Clear pending updates
    const updates = { ...pendingUpdates.current };
    pendingUpdates.current = {};
    
    try {
      // Save immediately
      await saveToBackend(selectedProduct.id, updates);
      
      // Clear unsaved changes flag
      setHasUnsavedChanges(false);
      
      console.log('✅ Save completed successfully');
    } catch (error) {
      console.error('❌ Save failed:', error);
      
      // Restore pending updates if save failed
      pendingUpdates.current = updates;
    }
  }, [selectedProduct, saveToBackend]);

  // Time ago formatter utility
  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    
    if (seconds < 10) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  // Handle Edit Details - Open full-page detail overlay
  const handleEditDetails = useCallback((product) => {
    // ✅ Safety check: Validate product data
    if (!product) {
      console.error('❌ handleEditDetails: product is undefined');
      return;
    }
    if (!product.id) {
      console.error('❌ handleEditDetails: product.id is missing', product);
      return;
    }
    
    // Phase 3: Open full-page product detail overlay
    console.log('🔧 Opening full-page detail overlay for:', product.sku || product.id);
    setInitialDetailTab('general'); // ✅ Set general as initial tab for Edit Details
    setFullPageProductId(product.id);
    setShowFullPageDetail(true);
  }, []);

  // Handle Edit Pricing - Open product detail with Pricing tab active
  const handleEditPricing = useCallback((product) => {
    // ✅ Safety check: Validate product data
    if (!product) {
      console.error('❌ handleEditPricing: product is undefined');
      return;
    }
    if (!product.id) {
      console.error('❌ handleEditPricing: product.id is missing', product);
      return;
    }
    
    console.log('💰 Opening pricing tab for:', product.sku || product.id);
    setInitialDetailTab('pricing'); // ✅ Set pricing as initial tab
    setFullPageProductId(product.id);
    setShowFullPageDetail(true);
  }, []);

  // Phase 2: Column Resizing Handlers
  const handleColumnResizeStart = useCallback((columnId, startX, startWidth) => {
    setIsResizing(columnId);
    setResizeStartX(startX);
    setResizeStartWidth(startWidth);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const handleColumnResize = useCallback((clientX) => {
    if (!isResizing) return;
    
    const deltaX = clientX - resizeStartX;
    const newWidth = Math.max(50, resizeStartWidth + deltaX); // Minimum width of 50px
    
    setColumnSizing(prev => ({
      ...prev,
      [isResizing]: newWidth
    }));
  }, [isResizing, resizeStartX, resizeStartWidth]);

  const handleColumnResizeEnd = useCallback(() => {
    if (!isResizing) return;
    
    const finalWidth = columnSizing[isResizing];
    if (finalWidth && currentVariant) {
      // Save width change to current variant's ephemeral changes
      const updatedConfig = {
        ...currentVariant.config_json,
        columns: (currentVariant.config_json?.columns || []).map(col =>
          col.id === isResizing ? { ...col, width: finalWidth } : col
        )
      };
      
      saveEphemeralChanges({
        ...currentVariant,
        config_json: updatedConfig
      });
      
      toast.success('Column width saved', { duration: 1000 });
    }
    
    setIsResizing(null);
    setResizeStartX(0);
    setResizeStartWidth(0);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [isResizing, columnSizing, currentVariant, saveEphemeralChanges]);

  // Phase 2: Global mouse event handlers for column resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizing) {
        e.preventDefault();
        handleColumnResize(e.clientX);
      }
    };

    const handleMouseUp = () => {
      if (isResizing) {
        handleColumnResizeEnd();
      }
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('mouseleave', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [isResizing, handleColumnResize, handleColumnResizeEnd]);

  // Phase 2: Enhanced Tooltip Handlers with performance optimization
  const handleCellMouseEnter = useCallback((e, value, columnId) => {
    // Throttle tooltip calculations for performance
    if (isResizing || !value) return;
    
    const cellElement = e.currentTarget;
    const textElement = cellElement.querySelector('span, div, [class*="truncate"]');
    
    // Enhanced truncation detection
    if (textElement && (
      textElement.scrollWidth > textElement.clientWidth ||
      String(value).length > 50 // Show tooltip for long content regardless
    )) {
      const rect = cellElement.getBoundingClientRect();
      setHoveredCell({
        value: String(value || ''),
        columnId,
        isLongContent: String(value).length > 50
      });
      setTooltipPosition({
        x: rect.left + rect.width / 2,
        y: rect.top - 8
      });
    }
  }, [isResizing]);

  const handleCellMouseLeave = useCallback(() => {
    setHoveredCell(null);
  }, []);

  // Phase 2: Enhanced Double-click auto-fit with improved performance
  const handleColumnDoubleClick = useCallback((columnId, headerElement) => {
    const startTime = performance.now();
    const cells = document.querySelectorAll(`[data-column="${columnId}"]`);
    let maxWidth = 120; // Minimum width
    const measurementCache = new Map();
    
    cells.forEach(cell => {
      const textElement = cell.querySelector('span, div, [class*="truncate"]');
      if (textElement) {
        const text = textElement.textContent || '';
        
        // Use cache for common text measurements
        if (measurementCache.has(text)) {
          maxWidth = Math.max(maxWidth, measurementCache.get(text));
          return;
        }
        
        // Create temporary element to measure text width
        const temp = document.createElement('div');
        temp.style.cssText = `
          visibility: hidden;
          position: absolute;
          white-space: nowrap;
          font: ${window.getComputedStyle(textElement).font};
          padding: 0 16px;
        `;
        temp.textContent = text;
        document.body.appendChild(temp);
        
        const textWidth = Math.min(temp.offsetWidth + 32, 500); // Max 500px
        maxWidth = Math.max(maxWidth, textWidth);
        measurementCache.set(text, textWidth);
        
        document.body.removeChild(temp);
      }
    });
    
    setColumnSizing(prev => ({
      ...prev,
      [columnId]: maxWidth
    }));
    
    // Save to ephemeral changes with performance monitoring
    if (currentVariant && saveEphemeralChanges) {
      const updatedConfig = {
        ...currentVariant.config_json,
        columns: (currentVariant.config_json?.columns || []).map(col =>
          col.id === columnId ? { ...col, width: maxWidth } : col
        )
      };
      
      saveEphemeralChanges({
        ...currentVariant,
        config_json: updatedConfig
      });
      
      const endTime = performance.now();
      const resizeTime = endTime - startTime;
      
      toast.success(`Column auto-resized to ${maxWidth}px (${resizeTime.toFixed(1)}ms)`, { 
        duration: 1500 
      });
    }
  }, [currentVariant, saveEphemeralChanges]);

  // Handle Duplicate Product
  const handleDuplicateProduct = useCallback(async (product) => {
    try {
      // Get full product details
      const fullProduct = await api.get(`/api/v1/product-master/item/${product.id}/details?tenant_id=${MOCK_TENANT_ID}`);
      
      // Create new SKU with -COPY suffix
      const newSku = `${product.sku}-COPY`;
      
      // Create duplicate with new SKU
      const duplicateData = {
        ...fullProduct,
        sku: newSku,
        name: `${product.name || product.product_name} (Copy)`,
        status: 'DRAFT',
        tenant_id: MOCK_TENANT_ID
      };
      
      // Remove fields that shouldn't be duplicated
      delete duplicateData.id;
      delete duplicateData._id;
      delete duplicateData.created_at;
      delete duplicateData.updated_at;
      
      const response = await api.post(`/api/v1/product-master/item?tenant_id=${MOCK_TENANT_ID}`, duplicateData);
      
      // Add to local data
      setData(prev => [response, ...prev]);
      
      toast.success(`Created duplicate: ${newSku}`, {
        description: 'Product copied successfully. Edit as needed.'
      });
    } catch (error) {
      console.error('Error duplicating product:', error);
      toast.error('Failed to duplicate product', {
        description: error.response?.data?.detail || 'Check if SKU already exists'
      });
    }
  }, []);

  // Handle Archive Product
  const handleArchiveProduct = useCallback(async (product) => {
    try {
      await api.patch(`/api/v1/product-master/item/${product.id}?tenant_id=${MOCK_TENANT_ID}`, {
        status: 'ARCHIVED'
      });
      
      // Update local data
      setData(prev => prev.map(item => 
        item.id === product.id ? { ...item, status: 'ARCHIVED' } : item
      ));
      
      toast.success(`${product.sku} archived successfully`);
    } catch (error) {
      console.error('Error archiving product:', error);
      toast.error('Failed to archive product');
    }
  }, []);

  // Handle Delete Product
  const handleDeleteProduct = useCallback(async (product) => {
    if (!confirm(`Delete ${product.sku} - ${product.name}?\n\nThis action cannot be undone.`)) {
      return;
    }
    
    try {
      await api.delete(`/api/v1/product-master/item/${product.id}`);
      
      // Remove from local data
      setData(prev => prev.filter(item => item.id !== product.id));
      
      toast.success(`${product.sku} deleted successfully`);
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  }, []);

  // Handle Bulk Delete Products
  const handleBulkDelete = useCallback(async () => {
    const selectedProducts = Object.keys(selectedRows)
      .filter(key => selectedRows[key])
      .map(key => data[parseInt(key)])
      .filter(Boolean);
    
    if (selectedProducts.length === 0) {
      toast.error('No products selected');
      return;
    }
    
    try {
      const productIds = selectedProducts.map(p => p.id);
      
      await api.delete('/api/v1/products/bulk-delete', {
        data: {
          product_ids: productIds,
          tenant_id: MOCK_TENANT_ID
        }
      });
      
      // Remove deleted products from local data
      setData(prev => prev.filter(item => !productIds.includes(item.id)));
      
      // Clear selection
      setSelectedRows({});
      setShowDeleteConfirm(false);
      
      toast.success(`Successfully deleted ${selectedProducts.length} product${selectedProducts.length > 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error deleting products:', error);
      toast.error('Failed to delete products');
    }
  }, [selectedRows, data]);

  // CRITICAL FIX: Handle bulk archive
  const handleBulkArchive = useCallback(async () => {
    const selectedProducts = Object.keys(selectedRows)
      .filter(key => selectedRows[key])
      .map(key => data[parseInt(key)])
      .filter(Boolean);
    
    if (selectedProducts.length === 0) {
      toast.error('No products selected');
      return;
    }
    
    try {
      const productIds = selectedProducts.map(p => p.id);
      
      // Archive all selected products
      await api.patch(`/api/v1/products/bulk-archive?tenant_id=${MOCK_TENANT_ID}`, {
        product_ids: productIds,
        status: 'ARCHIVED'
      });
      
      // Update local data to reflect archived status
      setData(prev => prev.map(item => 
        productIds.includes(item.id) ? { ...item, status: 'ARCHIVED' } : item
      ));
      
      // Clear selection
      setSelectedRows({});
      
      toast.success(`Successfully archived ${selectedProducts.length} product${selectedProducts.length > 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error archiving products:', error);
      toast.error('Failed to archive products');
    }
  }, [selectedRows, data]);

  // CRITICAL FIX: Export selected products to CSV
  const handleExportSelected = useCallback(() => {
    const selectedProducts = Object.keys(selectedRows)
      .filter(key => selectedRows[key])
      .map(key => data[parseInt(key)])
      .filter(Boolean);
    
    if (selectedProducts.length === 0) {
      toast.error('No products selected for export');
      return;
    }
    
    try {
      // CSV headers
      const headers = [
        'SKU',
        'Product Name',
        'Category',
        'Brand',
        'Status',
        'Selling Price',
        'Cost Price',
        'Margin %',
        'Stock Level',
        'Reorder Point',
        'Supplier',
        'Lead Time (Days)',
        'Warehouse'
      ];
      
      // CSV rows
      const rows = selectedProducts.map(product => [
        product.sku || '',
        product.name || product.product_name || '',
        product.category || '',
        product.brand || '',
        product.status || '',
        product.selling_price || product.default_selling_price || 0,
        product.cost_price || product.purchase_price || 0,
        product.margin || product.gross_margin || 0,
        product.on_hand || product.qty_on_hand || 0,
        product.reorder_point || 0,
        product.supplier_name || product.supplier || '',
        product.lead_time_days || product.gr_processing_time_days || '',
        product.warehouse_location || product.default_warehouse || ''
      ]);
      
      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(field => {
          // Escape fields with commas or quotes
          const fieldStr = String(field);
          if (fieldStr.includes(',') || fieldStr.includes('"') || fieldStr.includes('\n')) {
            return `"${fieldStr.replace(/"/g, '""')}"`;
          }
          return fieldStr;
        }).join(','))
      ].join('\n');
      
      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const filename = `products_export_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Successfully exported ${selectedProducts.length} products to ${filename}`);
      console.log(`✅ Exported ${selectedProducts.length} products`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export products');
    }
  }, [selectedRows, data]);

  // Get selected products for display
  const getSelectedProducts = useCallback(() => {
    return Object.keys(selectedRows)
      .filter(key => selectedRows[key])
      .map(key => data[parseInt(key)])
      .filter(Boolean);
  }, [selectedRows, data]);

  // Column definitions for the smart grid
  // Generate dynamic columns from current variant and catalog
  const columns = useMemo(() => {
    const baseColumns = [
      // Selection column (always first)
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            className="h-4 w-4"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            className="h-4 w-4"
          />
        ),
        size: 40,
        enableSorting: false,
        enableColumnFilter: false,
        meta: { frozen: true, freezeIndex: 0 }
      }
    ];

    // Filter columns based on current variant visibility and RBAC
    const availableColumns = filterColumnsByRBAC(PRODUCT_COLUMN_CATALOG, currentUserRoles);
    let selectedColumnIds = currentVariant?.columns || availableColumns.map(col => col.id);
    
    // ============================================================
    // COMPONENT-BASED INVENTORY: Always include Type, Uses, and Available columns
    // ============================================================
    const requiredColumns = ['inventory.product_type', 'inventory.uses', 'inventory.available_to_sell'];
    for (const reqCol of requiredColumns) {
      if (!selectedColumnIds.includes(reqCol)) {
        // Insert after general.status or at position 5
        const statusIndex = selectedColumnIds.indexOf('general.status');
        const insertIndex = statusIndex !== -1 ? statusIndex + 1 : 5;
        selectedColumnIds = [
          ...selectedColumnIds.slice(0, insertIndex),
          reqCol,
          ...selectedColumnIds.slice(insertIndex)
        ];
      }
    }

    // Build dynamic columns from catalog
    const dynamicColumns = selectedColumnIds
      .map(columnId => {
        const catalogColumn = getColumnById(columnId);
        if (!catalogColumn) return null;

        // Get current width from variant config or columnSizing state
        const currentWidth = columnSizing[catalogColumn.id] || 
                           (currentVariant?.config_json?.columns?.find(c => c.id === catalogColumn.id)?.width) || 
                           catalogColumn.width || 120;

        // Phase 3: Check RBAC edit permissions for this field
        const fieldCategory = getFieldCategory(catalogColumn.id);
        const canEdit = canEditField(currentUserRoles, fieldCategory);
        const accessLevel = getFieldAccessLevel(currentUserRoles, fieldCategory);

        const tableColumn = {
          id: catalogColumn.id,
          accessorKey: catalogColumn.id.replace('.', '_'), // Safe accessor
          accessorFn: catalogColumn.accessor, // Use catalog accessor function
          // Color-coded headers based on column type
          header: ({ column }) => {
            const headerClass = catalogColumn.headerClass || 'text-gray-300';
            return (
              <span className={`text-xs uppercase tracking-wider ${headerClass}`}>
                {catalogColumn.label}
              </span>
            );
          },
          size: currentWidth,
          enableSorting: true,
          enableColumnFilter: true,
          enableGrouping: catalogColumn.section && true,
          enableResizing: true, // Phase 2: Enable resizing
          meta: {
            section: catalogColumn.section,
            type: catalogColumn.type,
            editable: catalogColumn.editable && canEdit, // Phase 3: RBAC-controlled editable
            frozen: catalogColumn.pinned === 'left',
            freezeIndex: catalogColumn.id === 'general.sku' ? 1 : 
                        catalogColumn.id === 'general.name' ? 2 : undefined,
            // Phase 3: RBAC metadata
            rbacAccessLevel: accessLevel,
            rbacCanEdit: canEdit,
            rbacFieldCategory: fieldCategory,
            // Header class for color-coded columns
            headerClass: catalogColumn.headerClass
          },
          cell: ({ getValue, row }) => {
            const value = catalogColumn.accessor(row.original);
            const isRestricted = !canEdit;
            
            let content;
            // Use catalog formatter if available
            if (catalogColumn.formatter) {
              // Phase 2 & Phase B: Handle different formatter types
              const pricingColumns = [
                'pricing.totalCost',        // Total Cost column for PricingDrawer
                'pricing.targetMargin', 
                'pricing.finalPrice',
                'pricing.wholesaleMargin',
                'pricing.retailMargin',
                'pricing.wholesalePrice',   // Wholesale Price for PricingDrawer
                'pricing.retailPrice'       // Retail Price for PricingDrawer
              ];
              
              if (pricingColumns.includes(catalogColumn.id)) {
                // Pricing columns get the context object
                const formatterContext = {
                  onPricingUpdate: handlePricingUpdate,
                  onWholesaleMarginUpdate: handleWholesaleMarginUpdate,
                  onRetailMarginUpdate: handleRetailMarginUpdate,
                  onOpenPricingDrawer: handleOpenPricingDrawer,
                  hasEditPermission: hasEditPermission,
                  isUpdating: isUpdating
                };
                content = catalogColumn.formatter(value, row.original, formatterContext);
              } else {
                // Existing columns get calcDeps array (backwards compatibility)
                content = catalogColumn.formatter(value, row.original, catalogColumn.calcDeps);
              }
            } else {
              // Default formatters by type
              switch (catalogColumn.type) {
                case 'money':
                  content = formatters.money(value);
                  break;
                case 'percent':
                  content = formatters.percent(value);
                  break;
                case 'number':
                  content = formatters.number(value);
                  break;
                case 'badge':
                  content = formatters.badge(value);
                  break;
                case 'calc':
                  content = formatters.calc(value, row.original, catalogColumn.calcDeps);
                  break;
                default:
                  // Inline editing for text fields
                  if (catalogColumn.editable) {
                    content = (
                      <EnhancedEditableCell
                        initialValue={value || ''}
                        onSave={(newValue) => handleCellSave(row.original.id, catalogColumn.id, newValue)}
                        type="text"
                        rowId={row.original.id}
                        columnId={catalogColumn.id}
                      />
                    );
                  } else {
                    content = <span className="truncate">{value || '-'}</span>;
                  }
              }
            }
            
            // Phase 3: Add RBAC visual indicators
            if (isRestricted && catalogColumn.editable) {
              return (
                <div className="flex items-center justify-between group">
                  <div className={`flex-1 ${accessLevel === 'read' ? 'opacity-75' : ''}`}>
                    {content}
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <span 
                      className="text-xs text-gray-400 ml-2"
                      title={getRestrictedFieldMessage(currentUserRoles, fieldCategory)}
                    >
                      🔒
                    </span>
                  </div>
                </div>
              );
            }
            
            return content;
          }
        };

        return tableColumn;
      })
      .filter(Boolean);

    // Actions column (always last) - Exact specification implementation
    const actionsColumn = {
      id: 'actions',
      header: ({ column }) => (
        <div className="flex items-center justify-center" title="Row actions">
          {/* No visible header, but accessible tooltip */}
        </div>
      ),
      size: 56,
      enableSorting: false,
      enableHiding: false,
      meta: { 
        sticky: 'right'
      },
      cell: ({ row }) => (
        <RowActions 
          row={row}
          onQuickView={handleQuickView}
          onEdit={handleEditDetails}
          onEditPricing={handleEditPricing}
          onDuplicate={handleDuplicateProduct}
          onArchive={handleArchiveProduct}
          onDelete={handleDeleteProduct}
        />
      )
    };

    return [...baseColumns, ...dynamicColumns, actionsColumn];
  }, [currentVariant, effectiveCosts, columnSizing, handleCellSave, handleQuickView, handleEditDetails, handleEditPricing, handleDuplicateProduct, handleArchiveProduct, handleDeleteProduct]);

  // Restore column sizing when variant changes
  useEffect(() => {
    if (currentVariant?.columnSizing) {
      setColumnSizing(currentVariant.columnSizing);
      console.log('📐 Restored column sizing from variant:', currentVariant.name, currentVariant.columnSizing);
    }
  }, [currentVariant]);

  // Personal Dashboard Filters State (Phase 4.4) - moved here before applyPersonalFilters
  const [personalFilters, setPersonalFilters] = useState({
    showLowMarginOnly: false,
    showCriticalStockOnly: false,
    showInactiveOnly: false,
    marginThreshold: 20,
    stockThreshold: 10
  });

  // ============================================================
  // COMPONENT-BASED INVENTORY: Product Type Filter
  // ============================================================
  const [productTypeFilter, setProductTypeFilter] = useState('all');

  // Apply personal filters to data (Phase 4.4) - moved here to fix initialization order
  const applyPersonalFilters = useCallback((data) => {
    let filtered = [...data];
    
    // Apply product type filter first
    if (productTypeFilter !== 'all') {
      filtered = filtered.filter(item => {
        const itemType = item.product_type || 'standard';
        return itemType === productTypeFilter;
      });
    }
    
    if (personalFilters.showLowMarginOnly) {
      filtered = filtered.filter(item => {
        const price = item.price || 0;
        const cost = item.effective_cost || 0;
        if (price === 0) return false;
        const margin = ((price - cost) / price) * 100;
        return margin < personalFilters.marginThreshold;
      });
    }
    
    if (personalFilters.showCriticalStockOnly) {
      filtered = filtered.filter(item => {
        const stock = item.on_hand || 0;
        return stock < personalFilters.stockThreshold;
      });
    }
    
    return filtered;
  }, [personalFilters, productTypeFilter]);

  // Phase 4.4: Apply personal filters to data (moved here before table config)
  const filteredData = useMemo(() => {
    return applyPersonalFilters(data);
  }, [data, applyPersonalFilters]);

  // Table configuration
  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      globalFilter,
      columnFilters,
      sorting,
      grouping,
      columnVisibility,
      rowSelection: selectedRows,
      columnSizing, // Phase 2: Include column sizing state
      pagination, // Add pagination state
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onGroupingChange: setGrouping,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setSelectedRows,
    onColumnSizingChange: setColumnSizing, // Phase 2: Handle column sizing changes
    onPaginationChange: setPagination, // Handle pagination changes
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getPaginationRowModel: getPaginationRowModel(), // Enable pagination
    enableRowSelection: true,
    enableMultiRowSelection: true,
    enableGrouping: true,
    enableColumnResizing: true, // Phase 2: Enable column resizing
    columnResizeMode: 'onChange', // Phase 2: Live resize mode
    manualPagination: false, // Let TanStack handle pagination
  });

  // Virtualization setup
  const { rows } = table.getRowModel();
  const parentRef = React.useRef();
  
  // Debug logging
  console.log('SmartGrid: Data length:', data.length);
  console.log('SmartGrid: Rows length:', rows.length);
  console.log('SmartGrid: Sample row:', rows[0]);
  
  // Show the loading spinner while data is being fetched in the background
  const showLoadingSpinner = loading && rows.length === 0;

  // Smart Grid Gap Fix - Refs for scroll synchronization
  const headerRef = useRef(null);
  const scrollRef = useRef(null);
  
  // Horizontal Scrolling - Viewport ref for scroll container
  const viewportRef = useRef(null);
  
  // Set computed width and robust resize handling
  useEffect(() => {
    const computedTotalPx = Math.max(1800, TOTAL_GRID_WIDTH);
    
    // Set total canvas width from measured columns (guarantees overflow)
    document.documentElement.style.setProperty('--grid-min-width', `${computedTotalPx}px`);
    console.log('🎯 Grid min-width set to:', computedTotalPx + 'px');
  }, [TOTAL_GRID_WIDTH]);

  // Custom horizontal bar sync and resize handling
  useEffect(() => {
    if (rows.length === 0) {
      return; // Wait for data to be loaded first
    }
    
    const setupHBar = setTimeout(() => {
      const computedColumnsWidth = () => Math.max(1800, TOTAL_GRID_WIDTH);
      
      // Set canvas width properties
      const updateCanvasWidth = () => {
        const w = computedColumnsWidth();
        document.documentElement.style.setProperty('--grid-min-width', `${w}px`);
        document.documentElement.style.setProperty('--cols-total-width', `${w}px`);
        console.log('📏 Canvas width updated:', w + 'px');
      };

      // Custom horizontal bar sync logic
      const initHBarSync = () => {
        const vp = document.getElementById('zb-grid-viewport');
        const bar = document.getElementById('zb-hbar');
        const thumb = document.getElementById('zb-hbar-thumb');
        
        if (!vp || !bar || !thumb) {
          console.log('⚠️ HBar elements not found');
          return;
        }

        const update = () => {
          const ratio = vp.clientWidth / vp.scrollWidth;
          const width = Math.max(40, Math.floor(bar.clientWidth * ratio));
          const maxX = bar.clientWidth - width;
          const scrollRatio = vp.scrollWidth > vp.clientWidth ? vp.scrollLeft / (vp.scrollWidth - vp.clientWidth) : 0;
          const x = Math.floor(scrollRatio * maxX);
          
          thumb.style.setProperty('--hbar-width', `${width}px`);
          thumb.style.setProperty('--hbar-x', `${x}px`);
        };

        vp.addEventListener('scroll', update);
        const vpResizeObserver = new ResizeObserver(update);
        const barResizeObserver = new ResizeObserver(update);
        vpResizeObserver.observe(vp);
        barResizeObserver.observe(bar);

        // Dragging the thumb
        let startX = 0, startLeft = 0, dragging = false;
        
        thumb.addEventListener('pointerdown', (e) => {
          dragging = true;
          startX = e.clientX;
          startLeft = vp.scrollLeft;
          thumb.setPointerCapture(e.pointerId);
        });
        
        thumb.addEventListener('pointermove', (e) => {
          if (!dragging) return;
          const barWidth = bar.clientWidth;
          const thumbWidth = thumb.clientWidth;
          const maxThumbX = barWidth - thumbWidth;
          const dx = e.clientX - startX;
          const pxToScroll = maxThumbX > 0 ? (vp.scrollWidth - vp.clientWidth) / maxThumbX : 0;
          vp.scrollLeft = Math.min(vp.scrollWidth, Math.max(0, startLeft + dx * pxToScroll));
        });
        
        thumb.addEventListener('pointerup', () => {
          dragging = false;
        });
        
        thumb.addEventListener('pointercancel', () => {
          dragging = false;
        });

        // Wheel → horizontal pan (nice on trackpads + mice)
        bar.addEventListener('wheel', (e) => {
          if (Math.abs(e.deltaX) + Math.abs(e.deltaY) === 0) return;
          vp.scrollLeft += (Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY);
          e.preventDefault();
        }, { passive: false });

        update();
        
        console.log('✅ Custom HBar sync initialized');
        
        // Store cleanup function
        window.zbHBarCleanup = () => {
          vp.removeEventListener('scroll', update);
          vpResizeObserver.disconnect();
          barResizeObserver.disconnect();
          console.log('🧹 HBar cleanup complete');
        };
      };

      // Initialize canvas width and HBar
      updateCanvasWidth();
      requestAnimationFrame(initHBarSync);
      
    }, 100);
    
    // Cleanup
    return () => {
      clearTimeout(setupHBar);
      if (window.zbHBarCleanup) {
        window.zbHBarCleanup();
        delete window.zbHBarCleanup;
      }
    };
  }, [rows.length, TOTAL_GRID_WIDTH]); // Depend on data and grid width

  // Horizontal Scrolling - Shift+wheel support (passive, no preventDefault)
  useEffect(() => {
    const vp = document.getElementById('zb-grid-viewport');
    if (!vp) return;
    
    // Optional: Shift+wheel → horizontal pan (don't preventDefault)
    const onWheel = (e) => {
      if (e.shiftKey) vp.scrollLeft += e.deltaY;
    };
    
    vp.addEventListener('wheel', onWheel, { passive: true });
    return () => vp.removeEventListener('wheel', onWheel);
  }, []);

  // Smart Grid Gap Fix - Calculate proper grid height
  const gridHeight = useMemo(() => {
    const headerH = 48;   // actual header height in px
    const toolbarH = 60;  // toolbar height
    const padding = 24;   // padding
    const availableHeight = zenMode 
      ? window.innerHeight - headerH - padding
      : window.innerHeight - headerH - toolbarH - padding - 200; // account for nav and other UI
    return Math.max(400, availableHeight);
  }, [zenMode]);

  // Smart Grid Gap Fix - Scroll synchronization
  const syncScroll = useCallback((e) => {
    const scrollLeft = e.target.scrollLeft;
    if (headerRef.current) {
      headerRef.current.scrollLeft = scrollLeft;
    }
  }, []);

  // Smart Grid Stability Patch - react-window virtualization with fixed sizing
  const listRef = useRef(null);
  const containerHeight = useMemo(() => {
    return Math.min(window.innerHeight - 200, rows.length * ROW_HEIGHT + 100); // +100 for header
  }, [rows.length]);

  // Recompute grid function for layout changes
  const recomputeGrid = useCallback(() => {
    try {
      if (listRef.current) {
        listRef.current.scrollToItem(0, 'start');
      }
      if (headerRef.current && scrollRef.current) {
        headerRef.current.scrollLeft = scrollRef.current.scrollLeft;
      }
    } catch (error) {
      console.warn('Grid recomputation error:', error);
    }
  }, []);

  // Smart Grid Stability Patch - Trigger recomputation on layout changes
  useEffect(() => {
    recomputeGrid();
  }, [currentVariant, recomputeGrid]);

  // Performance guardrails - Debounced column resize handler
  const debouncedResize = useCallback(
    debounce((columnId, newWidth) => {
      // Save column width to layout variant
      console.log(`Column ${columnId} resized to ${newWidth}px`);
      recomputeGrid();
    }, 200),
    [recomputeGrid]
  );

  // Helper function for debounce
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Phase 4.2: Performance Scaling - Chunked Data Fetching
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadedChunks, setLoadedChunks] = useState(new Set());
  const [dataCache, setDataCache] = useState(new Map());
  const [isLoadingChunk, setIsLoadingChunk] = useState(false);
  
  const CHUNK_SIZE = 200; // Records per batch - limited by demo API constraint
  const PREFETCH_THRESHOLD = 50; // Prefetch when within 50 records of end
  const MAX_CACHED_CHUNKS = 20; // Memory management: max chunks in memory
  const CACHE_CLEANUP_THRESHOLD = 25; // Cleanup when exceeding this many chunks
  
  // Helper function to fetch pricing data for products
  const fetchPricingData = useCallback(async (skus) => {
    try {
      const pricingPromises = skus.map(async (sku) => {
        try {
          const data = await api.get(`/api/v1/product_pricing/${sku}?tenant_id=tenant-123`);
          return { sku, pricing: data };
        } catch (error) {
          console.warn(`Failed to fetch pricing for ${sku}:`, error);
          return { sku, pricing: null };
        }
      });
      
      const pricingResults = await Promise.all(pricingPromises);
      const pricingMap = {};
      
      pricingResults.forEach(({ sku, pricing }) => {
        if (pricing) {
          pricingMap[sku] = {
            total_cost: pricing.cost_breakdown.total_cost,
            target_margin_percent: pricing.pricing.target_margin_percent,
            system_price: pricing.pricing.system_price,
            final_price: pricing.pricing.final_price,
            net_margin_percent: pricing.pricing.net_margin_percent,
            pricing_mode: pricing.pricing.pricing_mode,
            // Individual cost components for tooltips
            pricing_cost_breakdown: pricing.cost_breakdown,
            pricing_last_updated: pricing.metadata.updated_at
          };
        }
      });
      
      return pricingMap;
    } catch (error) {
      console.error('Error fetching pricing data:', error);
      return {};
    }
  }, []);

  // Initial data loading with performance optimization and pricing integration
  const fetchInitialData = useCallback(async () => {
    const startTime = performance.now();
    setLoading(true);
    
    try {
      // Fetch first chunk with total count - using demo API with pricing data
      const tenantId = localStorage.getItem('tenant_id') || 'tenant-123';
      console.log('🔍 SmartGrid fetchInitialData called');
      console.log('📋 Tenant ID:', tenantId);
      
      // Add cache-busting timestamp
      const timestamp = Date.now();
      const apiUrl = `/api/v1/product-master/list?tenant_id=${tenantId}&page=1&page_size=${CHUNK_SIZE}&_t=${timestamp}`;
      console.log('📡 API endpoint:', apiUrl);
      
      // API client returns data directly, not the full response
      const data = await api.get(apiUrl, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'X-Tenant-ID': tenantId
        }
      });
      
      console.log('✅ SmartGrid API call succeeded');
      console.log('🔍 Response has items?', !!data.items);
      
      const items = data.items || [];
      console.log('✅ SmartGrid received data from API');
      console.log('📊 Number of products fetched:', items.length);
      console.log('📋 First 3 product SKUs:', items.slice(0, 3).map(p => p.sku || p.sku_root).join(', '));
      console.log('🔍 DATA STRUCTURE DEBUG:');
      console.log('  - API Response Total Items:', items.length);
      console.log('  - First item from API:', items[0]);
      if (items[0]) {
        console.log('  - SKU field:', items[0].sku, typeof items[0].sku);
        console.log('  - Product name field:', items[0].product_name, typeof items[0].product_name);
        console.log('  - Name field:', items[0].name, typeof items[0].name);
        console.log('  - Title field:', items[0].title, typeof items[0].title);
        console.log('  - All keys in first item:', Object.keys(items[0]).join(', '));
        // CRITICAL DEBUG: Check pricing fields
        console.log('📊 PRICING DATA DEBUG:');
        console.log('  - wholesale_price:', items[0].wholesale_price, typeof items[0].wholesale_price);
        console.log('  - retail_price:', items[0].retail_price, typeof items[0].retail_price);
        console.log('  - cost_price:', items[0].cost_price, typeof items[0].cost_price);
      }
      
      // Items already contain pricing data from demo API - no need to merge
      const enrichedItems = items;
      const total = data.total_count || enrichedItems.length;
      
      setTotalRecords(total);
      setData(enrichedItems);
      setLoadedChunks(new Set([1]));
      
      // 🔍 CRITICAL DEBUG: Log raw data structure Smart Grid receives
      if (enrichedItems && enrichedItems.length > 0) {
        console.log('═══════════════════════════════════════════════════════════════');
        console.log('🔍 SMART GRID FIRST ROW RAW DATA:', enrichedItems[0]);
        console.log('🔍 SKU:', enrichedItems[0].sku);
        console.log('🔍 inventory object:', enrichedItems[0].inventory);
        console.log('🔍 inventory.current_stock:', enrichedItems[0].inventory?.current_stock);
        console.log('🔍 current_stock (top-level):', enrichedItems[0].current_stock);
        console.log('🔍 planning object:', enrichedItems[0].planning);
        console.log('🔍 planning.on_hand:', enrichedItems[0].planning?.on_hand);
        console.log('🔍 on_hand (top-level):', enrichedItems[0].on_hand);
        console.log('🔍 All keys in first row:', Object.keys(enrichedItems[0]));
        console.log('═══════════════════════════════════════════════════════════════');
      }
      
      // Cache first chunk
      const newCache = new Map();
      newCache.set(1, enrichedItems);
      setDataCache(newCache);
      
      // Performance logging
      const loadTime = performance.now() - startTime;
      console.log(`SmartGrid Performance: Initial load ${items.length} items in ${loadTime.toFixed(2)}ms`);
      
      if (loadTime > 2000) {
        console.warn('Performance target missed: Initial load should be ≤2000ms');
      }
      
      // Prefetch next chunk if dataset is large
      if (total > CHUNK_SIZE) {
        setTimeout(() => prefetchNextChunk(2), 100);
      }
      
      // Fetch effective costs for first chunk
      await fetchEffectiveCosts(items);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Prefetch next chunk for performance
  const prefetchNextChunk = useCallback(async (page) => {
    if (loadedChunks.has(page) || isLoadingChunk) return;
    
    const startTime = performance.now();
    setIsLoadingChunk(true);
    
    try {
      const response = await api.get(
        `/api/v1/product-master/list?tenant_id=tenant-123&page=${page}&page_size=${CHUNK_SIZE}`
      );
      
      const items = response.items || [];
      
      // Items already contain pricing data from demo API
      const enrichedItems = items;
      
      const loadTime = performance.now() - startTime;
      
      console.log(`SmartGrid Performance: Chunk ${page} loaded ${enrichedItems.length} items in ${loadTime.toFixed(2)}ms`);
      
      if (loadTime > 300) {
        console.warn(`Performance target missed: Chunk load should be ≤300ms (actual: ${loadTime.toFixed(2)}ms)`);
      }
      
      // Update cache with memory management
      setDataCache(prev => {
        const newCache = new Map(prev);
        newCache.set(page, enrichedItems);
        
        // Memory management: cleanup old chunks if too many cached
        if (newCache.size > CACHE_CLEANUP_THRESHOLD) {
          const sortedPages = Array.from(newCache.keys()).sort((a, b) => a - b);
          // Keep most recent chunks, remove oldest ones
          const chunksToRemove = sortedPages.slice(0, newCache.size - MAX_CACHED_CHUNKS);
          chunksToRemove.forEach(pageToRemove => {
            if (pageToRemove < page - 5) { // Don't remove recent chunks
              newCache.delete(pageToRemove);
            }
          });
          console.log(`SmartGrid: Memory cleanup - removed ${chunksToRemove.length} old chunks`);
        }
        
        return newCache;
      });
      
      setLoadedChunks(prev => {
        const newSet = new Set([...prev, page]);
        // Also cleanup loaded chunks set
        if (newSet.size > CACHE_CLEANUP_THRESHOLD) {
          const sortedPages = Array.from(newSet).sort((a, b) => a - b);
          const chunksToRemove = sortedPages.slice(0, newSet.size - MAX_CACHED_CHUNKS);
          chunksToRemove.forEach(pageToRemove => {
            if (pageToRemove < page - 5) {
              newSet.delete(pageToRemove);
            }
          });
        }
        return newSet;
      });
      
      // Merge with existing data for seamless experience
      setData(prevData => {
        const allChunks = [];
        for (let i = 1; i <= page; i++) {
          const chunkData = i === page ? items : dataCache.get(i) || [];
          allChunks.push(...chunkData);
        }
        return allChunks;
      });
      
    } catch (error) {
      console.error(`Error prefetching chunk ${page}:`, error);
    } finally {
      setIsLoadingChunk(false);
    }
  }, [loadedChunks, isLoadingChunk, dataCache]);
  
  // Virtual scrolling with prefetch logic
  const handleScroll = useCallback((scrollInfo) => {
    const { scrollTop, scrollHeight, clientHeight } = scrollInfo;
    const scrollPercentage = scrollTop / (scrollHeight - clientHeight);
    
    // Prefetch next chunk when 80% scrolled through current data
    if (scrollPercentage > 0.8) {
      const totalPages = Math.ceil(totalRecords / CHUNK_SIZE);
      const currentMaxPage = Math.max(...Array.from(loadedChunks));
      
      if (currentMaxPage < totalPages) {
        prefetchNextChunk(currentMaxPage + 1);
      }
    }
  }, [totalRecords, loadedChunks, prefetchNextChunk]);
  
  // Legacy data fetching for backward compatibility
  const fetchData = useCallback(async () => {
    // Use performance-optimized initial data fetch
    await fetchInitialData();
  }, [fetchInitialData]);
  
  // Phase 4.4: User Customization - Conditional Highlighting Helper
  const getRowHighlighting = useCallback((rowData) => {
    const price = rowData.price || 0;
    const cost = rowData.effective_cost || 0;
    
    if (price === 0) return null; // No highlighting for items without price
    
    const marginPercent = ((price - cost) / price) * 100;
    
    // Conditional highlights based on profit margin
    if (marginPercent < 10) {
      return { 
        backgroundColor: '#FEF2F2', // Light red for very low margin
        borderLeft: '3px solid #EF4444',
        borderColor: '#FECACA'
      };
    } else if (marginPercent < 20) {
      return { 
        backgroundColor: '#FFFBEB', // Light yellow for low margin  
        borderLeft: '3px solid #F59E0B',
        borderColor: '#FED7AA'
      };
    } else if (marginPercent > 50) {
      return { 
        backgroundColor: '#F0FDF4', // Light green for high margin
        borderLeft: '3px solid #22C55E',  
        borderColor: '#BBF7D0'
      };
    }
    
    return null; // Default styling
  }, []);
  
  // Fetch effective costs
  const fetchEffectiveCosts = useCallback(async (items) => {
    try {
      const costs = {};
      await Promise.all(items.map(async (item) => {
        if (item.type === 'variant') {
          try {
            const data = await api.get(`/api/v1/calc/effective-cost/${item.id}`);
            costs[item.id] = data;
          } catch (error) {
            costs[item.id] = { effective_cost: null };
          }
        }
      }));
      setEffectiveCosts(costs);
    } catch (error) {
      console.error('Error fetching effective costs:', error);
    }
  }, []); // Remove API dependency to prevent re-creation

  // Calculate profit margin
  const calculateProfitMargin = useCallback((item) => {
    if (item.type !== 'variant' || !item.price) return null;
    
    const effectiveCost = effectiveCosts[item.id]?.effective_cost;
    if (!effectiveCost) return null;
    
    const margin = ((item.price - effectiveCost) / item.price) * 100;
    return {
      margin_pct: margin,
      color: margin < 0 ? 'red' : margin < 15 ? 'amber' : 'green'
    };
  }, [effectiveCosts]);

  // Phase 2: Task 2 - Recalculate Pricing Functions
  const handleRecalculatePricing = useCallback(() => {
    // Check RBAC permissions
    const hasPermission = currentUserRoles.includes('finance') || 
                         currentUserRoles.includes('owner') ||
                         currentUserRoles.includes('ops') ||
                         currentUserRoles.includes('admin');
    
    if (!hasPermission) {
      toast.error('You do not have permission to recalculate pricing');
      return;
    }
    
    setShowRecalculateModal(true);
  }, [currentUserRoles]);

  // Product Wizard Handler
  const handleOpenProductWizard = useCallback(() => {
    console.log('🧙 Opening Product Wizard...');
    setShowProductWizard(true);
  }, []);

  // Product Wizard Complete Handler
  const handleProductWizardComplete = useCallback(async (newProduct) => {
    console.log('✅ Product wizard completed:', newProduct);
    console.log('🔄 Triggering product list refresh...');
    setShowProductWizard(false);
    
    // Force refresh the product list
    try {
      await fetchInitialData();
      console.log('✅ Product list refreshed successfully');
      toast.success(`Product created successfully: ${newProduct?.sku || newProduct?.sku_root || 'New Product'}`);
    } catch (error) {
      console.error('❌ Failed to refresh product list:', error);
      toast.warning('Product created but list refresh failed. Please refresh the page.');
    }
  }, [fetchInitialData]);

  // Bulk Upload Handler
  const handleBulkUpload = useCallback(() => {
    // Check RBAC permissions  
    const hasPermission = currentUserRoles.includes('owner') ||
                         currentUserRoles.includes('admin') ||
                         currentUserRoles.includes('ops') ||
                         currentUserRoles.includes('finance');
    
    if (!hasPermission) {
      toast.error('You do not have permission to upload products');
      return;
    }
    
    setShowBulkUpload(true);
  }, [currentUserRoles]);

  // Bulk Upload Complete Handler
  const handleBulkUploadComplete = useCallback((result) => {
    console.log('Bulk upload completed:', result);
    
    // Show success message
    if (result?.results) {
      const { created, updated, deleted } = result.results;
      toast.success(`Upload completed! Created: ${created}, Updated: ${updated}, Deleted: ${deleted}`);
    }
    
    // Refresh the grid data
    fetchInitialData();
    
    // Close modal
    setShowBulkUpload(false);
  }, [fetchInitialData]);

  const handleRecalculateConfirm = useCallback(async (options) => {
    setIsRecalculating(true);
    setShowRecalculateModal(false);
    
    try {
      // Determine which SKUs to recalculate
      let skusToRecalculate = [];
      const selectedSKUs = Object.keys(selectedRows).filter(key => selectedRows[key]).map(key => data[parseInt(key)]?.sku).filter(Boolean);
      
      switch (options.scope) {
        case 'selected':
          skusToRecalculate = selectedSKUs;
          break;
        case 'visible':
          skusToRecalculate = data.map(item => item.sku).filter(Boolean);
          break;
        case 'all':
          skusToRecalculate = 'all'; // Special indicator for all products
          break;
      }
      
      setRecalculateProgress(`Recalculating pricing for ${options.count} products...`);
      
      // Call the bulk calculate API
      const response = await api.post(`/api/v1/product_pricing/bulk_calculate?tenant_id=${MOCK_TENANT_ID}`, {
        skus: skusToRecalculate === 'all' ? null : skusToRecalculate,
        force_recalculate: true,
        update_manufacturing_costs: options.updateManufacturingCosts,
        reset_manual_overrides: options.resetManualOverrides
      });
      
      const { recalculated_count, calculation_time_ms } = response;
      
      // Refresh grid data
      await fetchInitialData();
      
      // Success feedback
      toast.success(`Successfully recalculated pricing for ${recalculated_count} products (${calculation_time_ms}ms)`);
      
      // Clear selection if we recalculated selected items
      if (options.scope === 'selected') {
        setSelectedRows({});
      }
      
    } catch (error) {
      console.error('Error recalculating pricing:', error);
      toast.error('Failed to recalculate pricing. Please try again.');
    } finally {
      setIsRecalculating(false);
      setRecalculateProgress('');
    }
  }, [selectedRows, data, currentUserRoles, fetchInitialData]);

  // Get counts for recalculate modal
  const getRecalculateCounts = useCallback(() => {
    const selectedCount = Object.values(selectedRows).filter(Boolean).length;
    const visibleCount = data.length;
    const totalCount = totalRecords || visibleCount;
    
    return { selectedCount, visibleCount, totalCount };
  }, [selectedRows, data.length, totalRecords]);

  // Functions moved above to be accessible by columns

  // Load data and user preferences on component mount
  useEffect(() => {
    fetchData();
    
    // Load user preferences from localStorage (mock tenant/user scoping)
    const userId = 'user-123'; // In real app, get from auth context
    const tenantId = MOCK_TENANT_ID;
    const prefKey = `grid-prefs-${tenantId}-${userId}`;
    
    try {
      const savedPrefs = localStorage.getItem(prefKey);
      if (savedPrefs) {
        const prefs = JSON.parse(savedPrefs);
        
        // Restore column visibility
        if (prefs.columnVisibility) {
          setColumnVisibility(prefs.columnVisibility);
        }
        
        // Restore sorting
        if (prefs.sorting) {
          setSorting(prefs.sorting);
        }
        
        // Restore grouping
        if (prefs.grouping) {
          setGrouping(prefs.grouping);
        }
        
        // Phase 2: Restore column sizing
        if (prefs.columnSizing) {
          setColumnSizing(prefs.columnSizing);
        }
        
        // Note: Column order would be restored through table.setColumnOrder()
        // but requires more complex implementation with the table instance
        
        console.log('📂 User preferences loaded:', prefs);
      }
    } catch (error) {
      console.log('Could not load user preferences:', error);
    }
  }, []);
  
  // CRITICAL FIX: Listen for product updates and refresh list
  useEffect(() => {
    const handleRefresh = async (event) => {
      console.log('🔄 refreshProductList event triggered', event?.detail);
      console.log('⏳ Waiting 2000ms before refreshing to allow database sync...');
      
      // Wait for database to sync
      await new Promise(resolve => setTimeout(resolve, 2000));
      console.log('✅ Wait complete, triggering refresh...');
      console.log('🔄 Starting SmartGrid refresh...');
      await fetchInitialData();
      console.log('✅ SmartGrid refresh complete');
    };
    
    const handleProductCreated = async (event) => {
      console.log('✨ productCreated event triggered', event?.detail);
      const productData = event?.detail || {};
      console.log('📋 Created product details:', {
        sku: productData.sku || productData.sku_root,
        name: productData.name,
        tenant_id: productData.tenant_id
      });
      
      // Tenant ID comparison
      const smartGridTenantId = localStorage.getItem('tenant_id') || 'tenant-123';
      console.log('🔑 Tenant ID Comparison:', {
        fromEvent: productData.tenant_id,
        fromSmartGrid: smartGridTenantId,
        match: productData.tenant_id === smartGridTenantId
      });
      
      console.log('⏳ Waiting 2000ms for database synchronization...');
      await new Promise(resolve => setTimeout(resolve, 2000));
      console.log('✅ Wait complete, triggering refresh...');
      
      console.log('🔄 Starting SmartGrid refresh after product creation...');
      await fetchInitialData();
      console.log('✅ SmartGrid refresh complete after product creation');
    };
    
    const handleProductUpdated = async (event) => {
      console.log('✏️ productUpdated event triggered', event?.detail);
      const productData = event?.detail || {};
      console.log('📋 Updated product details:', {
        sku: productData.sku || productData.sku_root,
        name: productData.name,
        id: productData.id
      });
      
      console.log('⏳ Waiting 2000ms for database synchronization...');
      await new Promise(resolve => setTimeout(resolve, 2000));
      console.log('✅ Wait complete, triggering refresh...');
      
      console.log('🔄 Starting SmartGrid refresh after product update...');
      await fetchInitialData();
      console.log('✅ SmartGrid refresh complete after product update');
    };
    
    console.log('👂 SmartGrid event listeners registered');
    window.addEventListener('refreshProductList', handleRefresh);
    window.addEventListener('productCreated', handleProductCreated);
    window.addEventListener('productUpdated', handleProductUpdated);
    
    return () => {
      console.log('🔇 SmartGrid event listeners removed');
      window.removeEventListener('refreshProductList', handleRefresh);
      window.removeEventListener('productCreated', handleProductCreated);
      window.removeEventListener('productUpdated', handleProductUpdated);
    };
  }, [fetchInitialData]);
  
  // CRITICAL FIX: Clear selections when page changes
  useEffect(() => {
    console.log('🔄 Page changed, clearing selections');
    setSelectedRows({});
  }, [currentPage]);
  
  // Save preferences when they change - Phase 2 Enhanced
  useEffect(() => {
    const userId = 'user-123';
    const tenantId = MOCK_TENANT_ID;
    const prefKey = `grid-prefs-${tenantId}-${userId}`;
    
    const prefs = {
      columnVisibility,
      sorting,
      columnOrder: table.getState().columnOrder,
      grouping: grouping,
      columnSizing, // Phase 2: Include column sizing
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem(prefKey, JSON.stringify(prefs));
    console.log('💾 Grid preferences saved:', prefs);
  }, [columnVisibility, sorting, grouping, columnSizing, table]);

  const selectedRowCount = Object.keys(selectedRows).length;

  // Enhanced Keyboard Navigation
  const [selectedCell, setSelectedCell] = useState({ row: -1, column: -1 });

  React.useEffect(() => {
    const handleKeyboard = (e) => {
      // Handle Escape key to exit Zen mode
      if (e.key === 'Escape' && zenMode) {
        setZenMode(false);
        return;
      }

      // Handle keyboard navigation in grid
      if (rows.length > 0 && selectedCell.row >= 0) {
        const maxRow = rows.length - 1;
        const maxCol = table && table.getVisibleFlatColumns ? table.getVisibleFlatColumns().length - 1 : 0;

        switch (e.key) {
          case 'ArrowUp':
            e.preventDefault();
            setSelectedCell(prev => ({ 
              ...prev, 
              row: Math.max(0, prev.row - 1) 
            }));
            break;
          case 'ArrowDown':
            e.preventDefault();
            setSelectedCell(prev => ({ 
              ...prev, 
              row: Math.min(maxRow, prev.row + 1) 
            }));
            break;
          case 'ArrowLeft':
            e.preventDefault();
            setSelectedCell(prev => ({ 
              ...prev, 
              column: Math.max(0, prev.column - 1) 
            }));
            break;
          case 'ArrowRight':
            e.preventDefault();
            setSelectedCell(prev => ({ 
              ...prev, 
              column: Math.min(maxCol, prev.column + 1) 
            }));
            break;
          case 'Tab':
            e.preventDefault();
            if (e.shiftKey) {
              // Shift+Tab - move left
              setSelectedCell(prev => ({ 
                ...prev, 
                column: Math.max(0, prev.column - 1) 
              }));
            } else {
              // Tab - move right
              setSelectedCell(prev => ({ 
                ...prev, 
                column: Math.min(maxCol, prev.column + 1) 
              }));
            }
            break;
          case 'Enter':
            e.preventDefault();
            // Trigger edit mode for selected cell
            const selectedElement = document.querySelector(`[data-cell="${selectedCell.row}-${selectedCell.column}"]`);
            if (selectedElement) {
              selectedElement.click();
            }
            break;
          case 's':
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              // Save current row and maintain focus
              console.log('Saving current row...');
              // Keep focus on current cell after save
            }
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyboard);
    return () => document.removeEventListener('keydown', handleKeyboard);
  }, [zenMode, setZenMode, rows, selectedCell, table]);

  // recomputeGrid function moved above to fix dependency order

  // Removed complex Row component to eliminate Object.values errors

  return (
    <div className={`flex flex-col ${zenMode ? 'fixed inset-0 z-50 bg-white dark:bg-gray-900 h-screen w-screen' : 'h-full'} transition-colors duration-200`}>
      {/* Debug removed - data rendering working */}
      {/* Header Bar - Compact */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-300 dark:border-gray-600 p-2 transition-colors duration-200">
        {/* SINGLE CONTROL ROW - All filters and actions in one line */}
        <div className="flex items-center justify-between gap-3">
          
          {/* LEFT SIDE: Search + Filters */}
          <div className="flex items-center gap-2 flex-1">
            {/* ============================================================ */}
            {/* COMPONENT-BASED INVENTORY: Product Type Filter Tabs */}
            {/* ============================================================ */}
            <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5 mr-2">
              <button
                onClick={() => setProductTypeFilter('all')}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                  productTypeFilter === 'all'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setProductTypeFilter('raw_material')}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${
                  productTypeFilter === 'raw_material'
                    ? 'bg-orange-500/20 text-orange-600 dark:text-orange-400 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <span>🧱</span> Raw
              </button>
              <button
                onClick={() => setProductTypeFilter('finished_product')}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${
                  productTypeFilter === 'finished_product'
                    ? 'bg-green-500/20 text-green-600 dark:text-green-400 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <span>🎁</span> Finished
              </button>
              <button
                onClick={() => setProductTypeFilter('standard')}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${
                  productTypeFilter === 'standard'
                    ? 'bg-gray-500/20 text-gray-600 dark:text-gray-300 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <span>📦</span> Standard
              </button>
            </div>
            
            {/* Search */}
            <div className="relative w-56">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400 dark:text-gray-500" />
              <Input
                placeholder="Search SKU or product..."
                value={globalFilter ?? ''}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="pl-7 h-7 text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Category Filter */}
            <select
              value={columnFilters.find(f => f.id === 'category')?.value || 'all'}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'all') {
                  setColumnFilters(prev => prev.filter(f => f.id !== 'category'));
                } else {
                  setColumnFilters(prev => [
                    ...prev.filter(f => f.id !== 'category'),
                    { id: 'category', value }
                  ]);
                }
              }}
              className="h-7 px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[110px]"
              style={{ colorScheme: 'dark' }}
            >
              <option value="all">All Categories</option>
              <option value="Buckets">Buckets</option>
              <option value="Bowls">Bowls</option>
              <option value="Cheese Chews">Cheese Chews</option>
              <option value="Wood">Wood</option>
              <option value="Dog Toys">Dog Toys</option>
              <option value="Dog Food">Dog Food</option>
              <option value="Pet Supplies">Pet Supplies</option>
              <option value="Raw Materials">Raw Materials</option>
            </select>

            {/* Status Filter (includes Low Stock, Low Margin) */}
            <select
              value={columnFilters.find(f => f.id === 'status')?.value || (personalFilters.showCriticalStockOnly ? 'low_stock' : personalFilters.showLowMarginOnly ? 'low_margin' : 'all')}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'all') {
                  setColumnFilters(prev => prev.filter(f => f.id !== 'status'));
                  setPersonalFilters(prev => ({...prev, showLowMarginOnly: false, showCriticalStockOnly: false}));
                } else if (value === 'low_stock') {
                  setPersonalFilters(prev => ({...prev, showCriticalStockOnly: true, showLowMarginOnly: false}));
                  setColumnFilters(prev => prev.filter(f => f.id !== 'status'));
                } else if (value === 'low_margin') {
                  setPersonalFilters(prev => ({...prev, showLowMarginOnly: true, showCriticalStockOnly: false}));
                  setColumnFilters(prev => prev.filter(f => f.id !== 'status'));
                } else {
                  setPersonalFilters(prev => ({...prev, showLowMarginOnly: false, showCriticalStockOnly: false}));
                  setColumnFilters(prev => [
                    ...prev.filter(f => f.id !== 'status'),
                    { id: 'status', value }
                  ]);
                }
              }}
              className="h-7 px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[100px]"
              style={{ colorScheme: 'dark' }}
            >
              <option value="all">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="low_stock">⚠ Low Stock</option>
              <option value="low_margin">$ Low Margin</option>
              <option value="ARCHIVED">Archived</option>
            </select>

            {/* Show per page */}
            <select
              value={pagination.pageSize.toString()}
              onChange={(e) => {
                setPagination(prev => ({
                  ...prev,
                  pageSize: Number(e.target.value),
                  pageIndex: 0,
                }));
              }}
              className="h-7 px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 w-16"
              style={{ colorScheme: 'dark' }}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            
            {/* Preset Tabs (Pill Buttons) */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
              {presetVariants.map(preset => (
                <button
                  key={preset.id}
                  onClick={() => {
                    setCustomColumns(null); // Clear custom selection
                    applyVariant(preset);
                  }}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    currentVariant?.id === preset.id && !customColumns
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
                  data-testid={`preset-${preset.name.toLowerCase()}`}
                >
                  {preset.name}
                </button>
              ))}
              {customColumns && (
                <button
                  className="px-3 py-1 text-xs font-medium rounded-md bg-purple-500 text-white shadow-sm"
                  data-testid="preset-custom"
                >
                  Custom
                </button>
              )}
            </div>
            
            {/* Columns Customization Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowColumnPanel(true)}
              className={`px-2 py-1 h-7 text-xs rounded ${
                customColumns 
                  ? 'border-purple-500 text-purple-600 dark:text-purple-400' 
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
              }`}
              title="Customize columns"
              data-testid="columns-customize-btn"
            >
              <Settings className="w-3.5 h-3.5" />
            </Button>
          </div>
          
          {/* RIGHT SIDE: Actions */}
          <div className="flex items-center gap-2">
            {/* Add Product */}
            <Button
              variant="default"
              size="sm"
              onClick={handleOpenProductWizard}
              className="gap-1 px-3 py-1.5 h-7 text-xs font-medium rounded text-white"
              style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
              title="Create new product"
            >
              <Plus className="w-3 h-3" />
              Product
            </Button>
            
            {/* Bulk Upload */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkUpload}
              className="px-3 py-1.5 h-7 text-xs font-medium rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 gap-1"
              disabled={loading || !currentUserRoles.some(role => ['owner', 'admin', 'ops', 'finance'].includes(role))}
              title="Bulk upload products from CSV"
              data-testid="bulk-upload-import-btn"
            >
              <Upload className="w-3 h-3" />
              Import
            </Button>
            
            {/* Export CSV */}
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const response = await fetch('/api/v1/products/bulk/export-csv');
                  if (!response.ok) throw new Error('Export failed');
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  const today = new Date().toISOString().split('T')[0];
                  a.download = `inventory_export_${today}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  window.URL.revokeObjectURL(url);
                  document.body.removeChild(a);
                  toast.success('CSV exported successfully');
                } catch (error) {
                  console.error('Export error:', error);
                  toast.error('Failed to export CSV');
                }
              }}
              className="px-3 py-1.5 h-7 text-xs font-medium rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 gap-1"
              disabled={loading}
              title="Export inventory to CSV"
            >
              <Download className="w-3 h-3" />
              Export
            </Button>
            
            {/* Smart Grid indicator - active state */}
            <Button
              variant="outline"
              size="sm"
              className="px-3 py-1.5 h-7 text-xs font-medium rounded bg-blue-600 text-white border-blue-600 gap-1"
              title="Smart Grid View"
            >
              <LayoutGrid className="w-3 h-3" />
              Smart Grid
            </Button>
            
            {/* Divider */}
            <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1"></div>
            
            {/* Settings */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowViewSettings(true)}
              className="w-7 h-7 rounded bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
              title="View Settings"
            >
              <Settings className="w-3 h-3" />
            </Button>
            
            {/* Refresh */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => fetchInitialData()}
              className="w-7 h-7 rounded bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
              title="Refresh data"
              disabled={loading}
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            
            {/* Zen Mode */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setZenMode(!zenMode)}
              className={`w-7 h-7 rounded ${
                zenMode
                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700'
                  : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              title={zenMode ? "Exit Zen Mode (Esc)" : "Zen Mode"}
            >
              {zenMode ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
            </Button>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedRowCount > 0 && (
          <div className="flex items-center justify-between p-3 rounded-lg" style={{backgroundColor: '#EFF4F9', border: '1px solid #E5E5E5'}}>
            <span className="text-sm font-medium" style={{color: '#333333'}}>
              {selectedRowCount} selected
            </span>
            <div className="flex items-center gap-2">
              <Button 
                size="sm" 
                className="bg-gray-600 text-white hover:bg-gray-700 border-gray-600"
                onClick={() => handleBulkArchive()}
              >
                <Archive className="w-4 h-4 mr-1" />
                Archive
              </Button>
              <Button 
                size="sm"
                className="bg-red-600 text-white hover:bg-red-700 border-red-600"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
              <Button 
                size="sm" 
                className="bg-blue-600 text-white hover:bg-blue-700 border-blue-600"
                onClick={handleExportSelected}
              >
                Export Selected
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Smart Grid Area - SAP-parity stable table structure */}
      <div className={`flex-1 ${zenMode ? 'h-[calc(100vh-120px)]' : 'h-[calc(100vh-300px)] min-h-[400px]'}`}>
        <div className="zb-grid-shell zb-grid-host h-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded overflow-hidden transition-colors duration-200">
          <div className="zg-grid-wrap zb-grid-wrap" ref={parentRef}>
            {rows.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-center">
                {showLoadingSpinner ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Loading products...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3 max-w-md">
                    <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                      <Package className="w-6 h-6 text-gray-400" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">No products found</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                        Get started by adding your first product to the catalog or check your search filters.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button className="h-7 px-3 text-xs bg-blue-600 hover:bg-blue-700 text-white">
                        <Plus className="w-3 h-3 mr-1" />
                        Add Product
                      </Button>
                      <Button variant="outline" className="h-7 px-3 text-xs" onClick={() => setGlobalFilter('')}>
                        Clear Filters
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <React.Fragment>
                {/* PROPER STICKY TABLE CONTAINER */}
                <div 
                  className="table-container"
                  style={{ 
                    height: containerHeight,
                    overflow: 'auto',
                    position: 'relative',
                    border: '1px solid #e5e7eb'
                  }}
                >
                  <table className="min-w-full bg-white dark:bg-gray-900" style={{ position: 'relative' }}>
                    <thead className="sticky top-0 z-20 bg-gray-50 dark:bg-gray-800 border-b-2 border-gray-200 dark:border-gray-700">
                      {table?.getHeaderGroups?.()?.map(headerGroup => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers?.map((header, index) => {
                            const isCheckbox = header.id === 'select';
                            const isSKU = header.id === 'sku' || header.id === 'general.sku';
                            
                            return (
                              <th
                                key={header.id}
                                className={`px-3 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors ${
                                  isCheckbox || isSKU ? 'bg-gray-50 dark:bg-gray-800' : ''
                                }`}
                                style={{ 
                                  position: (isCheckbox || isSKU) ? 'sticky' : 'static',
                                  left: isCheckbox ? 0 : isSKU ? '48px' : 'auto',
                                  zIndex: (isCheckbox || isSKU) ? 25 : 20,
                                  height: '48px',
                                  minWidth: isCheckbox ? '48px' : isSKU ? '120px' : '120px'
                                }}
                                onClick={header.column.getToggleSortingHandler?.()}
                              >
                                {header.isPlaceholder ? null : (
                                  <div className="flex items-center gap-2">
                                    <span className="truncate">
                                      {flexRender(header.column.columnDef.header, header.getContext())}
                                    </span>
                                    {header.column.getCanSort?.() && (
                                      <span className="opacity-70">
                                        {header.column.getIsSorted() === 'asc' ? (
                                          <ArrowUp className="h-3 w-3 text-blue-600" />
                                        ) : header.column.getIsSorted() === 'desc' ? (
                                          <ArrowDown className="h-3 w-3 text-blue-600" />
                                        ) : (
                                          <ArrowUpDown className="h-3 w-3 text-gray-400" />
                                        )}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </th>
                            );
                          })}
                        </tr>
                      ))}
                    </thead>

                    <tbody>
                      {rows?.map((row, rowIndex) => (
                        <tr 
                          key={row.id} 
                          className="bg-white dark:bg-gray-900 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-all duration-200 cursor-pointer border-b border-gray-200 dark:border-gray-700 group"
                          style={{ height: '36px' }}
                          onClick={() => {
                            // CRITICAL FIX: Row click opens product detail drawer
                            if (row.original && handleQuickView) {
                              handleQuickView(row.original);
                            }
                          }}
                          data-testid={`product-row-${row.original?.sku || row.id}`}
                        >
                          {row.getVisibleCells?.()?.map((cell, cellIndex) => {
                            const isCheckbox = cell.column.id === 'select';
                            const isSKU = cell.column.id === 'sku' || cell.column.id === 'general.sku';
                            const isActions = cell.column.id === 'actions';
                            
                            return (
                              <td 
                                key={cell.id}
                                className={`px-3 py-2 text-xs text-gray-900 dark:text-gray-100 ${
                                  isCheckbox || isSKU ? 'bg-white dark:bg-gray-900' : ''
                                } ${
                                  cell.column.columnDef.type === 'badge' 
                                    ? '' // No truncation for badges 
                                    : 'overflow-hidden text-ellipsis whitespace-nowrap' // Truncate other content
                                }`}
                                style={{ 
                                  position: (isCheckbox || isSKU) ? 'sticky' : 'static',
                                  left: isCheckbox ? 0 : isSKU ? '48px' : 'auto',
                                  zIndex: (isCheckbox || isSKU) ? 15 : 1,
                                  height: '32px', 
                                  lineHeight: '18px',
                                  minWidth: isCheckbox ? '48px' : isSKU ? '120px' : '120px'
                                }}
                                onClick={(e) => {
                                  // Only stop propagation for checkbox and actions columns
                                  // Let other cells propagate to row click for product detail
                                  if (isCheckbox || isActions) {
                                    e.stopPropagation();
                                  }
                                }}
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls - Compact */}
                {rows.length > 0 && (
                  <div className="flex items-center justify-between px-3 py-2 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                    {/* Left side - Info text */}
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      Showing {pagination.pageIndex * pagination.pageSize + 1} to{' '}
                      {Math.min((pagination.pageIndex + 1) * pagination.pageSize, table.getFilteredRowModel().rows.length)} of{' '}
                      {table.getFilteredRowModel().rows.length} items
                    </div>

                    {/* Right side - Pagination buttons */}
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => table.setPageIndex(0)}
                        disabled={!table.getCanPreviousPage()}
                        className="h-6 px-2 text-xs"
                      >
                        First
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                        className="h-6 px-2 text-xs"
                      >
                        Previous
                      </Button>

                      {/* Page numbers */}
                      <div className="flex items-center gap-1">
                        {Array.from({ length: table.getPageCount() }, (_, i) => i)
                          .filter(pageNum => {
                            // Show first page, last page, current page, and pages around current
                            const current = table.getState().pagination.pageIndex;
                            return (
                              pageNum === 0 ||
                              pageNum === table.getPageCount() - 1 ||
                              Math.abs(pageNum - current) <= 1
                            );
                          })
                          .map((pageNum, idx, arr) => {
                            // Add ellipsis if there's a gap
                            const prevPageNum = arr[idx - 1];
                            const showEllipsis = prevPageNum !== undefined && pageNum - prevPageNum > 1;

                            return (
                              <React.Fragment key={pageNum}>
                                {showEllipsis && (
                                  <span className="px-1 text-gray-400 text-xs">...</span>
                                )}
                                <Button
                                  variant={table.getState().pagination.pageIndex === pageNum ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() => table.setPageIndex(pageNum)}
                                  className={`h-6 w-6 p-0 text-xs ${
                                    table.getState().pagination.pageIndex === pageNum
                                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                                      : ''
                                  }`}
                                >
                                  {pageNum + 1}
                                </Button>
                              </React.Fragment>
                            );
                          })}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                        className="h-6 px-2 text-xs"
                      >
                        Next
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                        disabled={!table.getCanNextPage()}
                        className="h-6 px-2 text-xs"
                      >
                        Last
                      </Button>
                    </div>
                  </div>
                )}
              </React.Fragment>
            )}
          </div>
        </div>
      </div>

      {/* Column Manager */}
      <Sheet open={showColumnManager} onOpenChange={setShowColumnManager}>
        <SheetContent side="right" className="w-80">
          <SheetHeader>
            <SheetTitle>Manage Columns</SheetTitle>
          </SheetHeader>
          
          {/* Reset Columns Button */}
          <div className="mt-6 mb-4">
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full gap-2"
              onClick={() => {
                // Reset to default visibility
                table.resetColumnVisibility();
                // Save preference (mock for now)
                localStorage.setItem('grid-column-visibility', JSON.stringify({}));
              }}
            >
              <Package className="w-4 h-4" />
              Reset Columns to Default
            </Button>
          </div>

          <div className="space-y-4">
            {table.getAllLeafColumns().map((column) => (
              <div key={column.id} className="flex items-center space-x-2">
                <Checkbox
                  checked={column.getIsVisible()}
                  onCheckedChange={(value) => column.toggleVisibility(!!value)}
                />
                <label className="text-sm font-medium">
                  {typeof column.columnDef.header === 'string' 
                    ? column.columnDef.header 
                    : column.id}
                </label>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {/* Quick View/Editor Drawer */}
      <Sheet open={showQuickView} onOpenChange={(open) => {
        if (!open) {
          // Guard: If modal is already handling the action, don't interfere
          if (showUnsavedModal) {
            console.log('🛡️ Sheet onOpenChange BLOCKED - modal is handling it');
            return;
          }
          // User trying to close the drawer
          handleDrawerClose();
        } else {
          setShowQuickView(true);
        }
      }}>
        <SheetContent className="w-[600px] sm:w-[700px] sm:max-w-none overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                {selectedProduct ? `${selectedProduct.sku} - ${selectedProduct.name}` : 'Product Details'}
              </div>
              
              {/* Save Status Indicators */}
              <div className="flex items-center gap-3">
                {isSaving && (
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </div>
                )}
                
                {hasUnsavedChanges && !isSaving && (
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs">
                      Unsaved Changes
                    </span>
                    <button
                      onClick={handleManualSave}
                      className="px-3 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700"
                    >
                      Save All
                    </button>
                  </div>
                )}
                
                {!hasUnsavedChanges && !isSaving && lastSaved && (
                  <div className="flex items-center gap-2 text-xs text-green-600">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Saved {formatTimeAgo(lastSaved)}</span>
                  </div>
                )}
              </div>
            </SheetTitle>
          </SheetHeader>
          
          {selectedProduct && (
            <div className="mt-6">
              {/* Tab Navigation - Updated for BOM/Costing System */}
              <div className="flex flex-wrap gap-1 border-b mb-6">
                {[
                  { id: 'general', label: 'General', icon: Package },
                  { id: 'purchasing', label: 'Purchasing', icon: Search },
                  { id: 'sales', label: 'Sales', icon: DollarSign },
                  { id: 'inventory', label: 'Inventory', icon: Archive },
                  { id: 'channels', label: 'Channels', icon: Package },
                  // BOM tab - only for finished products
                  ...(selectedProduct?.product_type !== 'raw_material' ? [
                    { id: 'bom', label: 'BOM', icon: Package }
                  ] : []),
                  { id: 'pricing', label: 'Pricing', icon: DollarSign },
                  { id: 'competition', label: 'Competition', icon: Trophy },
                  { id: 'suppliers', label: 'Suppliers', icon: Package },
                ].map(({ id, label, icon: Icon }) => (
                  <Button
                    key={id}
                    variant={activeDrawerTab === id ? "default" : "ghost"}
                    size="sm"
                    className="gap-1"
                    onClick={() => {
                      console.log('🔘 DRAWER TAB BUTTON CLICKED:', id);
                      console.log('🔘 Current state:', { activeDrawerTab, pricingTabDirty });
                      handleDrawerTabSwitch(id);
                    }}
                  >
                    <Icon className="w-3 h-3" />
                    <span className="hidden sm:inline">{label}</span>
                  </Button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="space-y-6">
                {/* Unsaved Changes Warning Banner */}
                {hasUnsavedChanges && (
                  <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium text-orange-800 dark:text-orange-200">
                          You have unsaved changes
                        </span>
                      </div>
                      <button
                        onClick={handleManualSave}
                        disabled={isSaving}
                        className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                      >
                        {isSaving ? (
                          <>
                            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-3 h-3" />
                            Save Now
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
                
                {activeDrawerTab === 'general' && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white">General Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">SKU</Label>
                        <Input
                          value={selectedProduct.sku || ''}
                          onChange={(e) => handleDrawerSave('sku', e.target.value)}
                          className="font-mono"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Product Name</Label>
                        <Input
                          value={selectedProduct.name || ''}
                          onChange={(e) => handleDrawerSave('name', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Category</Label>
                        <Select
                          value={selectedProduct.category || ''}
                          onValueChange={(value) => handleDrawerSave('category', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Buckets">Buckets</SelectItem>
                            <SelectItem value="Bowls">Bowls</SelectItem>
                            <SelectItem value="Cheese Chews">Cheese Chews</SelectItem>
                            <SelectItem value="Wood">Wood</SelectItem>
                            <SelectItem value="Dog Toys">Dog Toys</SelectItem>
                            <SelectItem value="Dog Food">Dog Food</SelectItem>
                            <SelectItem value="Pet Supplies">Pet Supplies</SelectItem>
                            <SelectItem value="Raw Materials">Raw Materials</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {/* Product Type - Critical for BOM/Costing System */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Product Type</Label>
                        <Select
                          value={selectedProduct.product_type || 'finished_product'}
                          onValueChange={(value) => handleDrawerSave('product_type', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="raw_material">Raw Material</SelectItem>
                            <SelectItem value="finished_product">Finished Product</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Raw materials have landed cost. Finished products use BOM for cost calculation.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status</Label>
                        <Select
                          value={selectedProduct.status || 'ACTIVE'}
                          onValueChange={(value) => handleDrawerSave('status', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ACTIVE">Active</SelectItem>
                            <SelectItem value="ARCHIVED">Archived</SelectItem>
                            <SelectItem value="DRAFT">Draft</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {/* Amazon ASIN - Required for BSR Tracking */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Amazon ASIN
                          <span className="ml-1 text-xs text-blue-500">(for BSR)</span>
                        </Label>
                        <Input
                          value={selectedProduct.amazon_asin || drawerData.amazon_asin || ''}
                          onChange={(e) => handleDrawerSave('amazon_asin', e.target.value.toUpperCase())}
                          placeholder="e.g., B0D96QDKQC"
                          maxLength={10}
                          className="font-mono"
                          data-testid="amazon-asin-input"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Required for BSR tracking in Competition tab
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Description</Label>
                      <textarea
                        value={drawerData.description || selectedProduct.description || ''}
                        onChange={(e) => {
                          // Update local drawer data immediately for responsive typing
                          setDrawerData(prev => ({ ...prev, description: e.target.value }));
                          // Debounced save to backend
                          handleDrawerSave('description', e.target.value);
                        }}
                        className="min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                        placeholder="Enter product description..."
                      />
                    </div>
                  </div>
                )}

                {/* ============================================================ */}
                {/* COMPONENT-BASED INVENTORY: Product Type Tab */}
                {/* ============================================================ */}
                {activeDrawerTab === 'type' && (
                  <ProductTypeTab 
                    product={selectedProduct} 
                    onSave={handleDrawerSave}
                    onProductTypeChange={async (newType, typeData) => {
                      // Handle product type change - may need to create new records
                      console.log('📦 Product type changing to:', newType, typeData);
                      handleDrawerSave('product_type', newType);
                      if (typeData.component) {
                        handleDrawerSave('component', typeData.component);
                      }
                      if (typeData.unit) {
                        handleDrawerSave('unit', typeData.unit);
                      }
                      if (typeData.current_stock !== undefined) {
                        handleDrawerSave('current_stock', typeData.current_stock);
                      }
                      if (typeData.reorder_point !== undefined) {
                        handleDrawerSave('reorder_point', typeData.reorder_point);
                      }
                    }}
                  />
                )}

                {activeDrawerTab === 'purchasing' && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white">Purchasing Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Supplier Price</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={selectedProduct.supplier_price || ''}
                          onChange={(e) => handleDrawerSave('supplier_price', parseFloat(e.target.value))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Unit of Measure</Label>
                        <Select
                          value={selectedProduct.unit_of_measure || ''}
                          onValueChange={(value) => handleDrawerSave('unit_of_measure', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="each">Each</SelectItem>
                            <SelectItem value="piece">Piece</SelectItem>
                            <SelectItem value="PCS">Pieces (PCS)</SelectItem>
                            <SelectItem value="pack">Pack</SelectItem>
                            <SelectItem value="PACK">Pack (PACK)</SelectItem>
                            <SelectItem value="SET">Set (SET)</SelectItem>
                            <SelectItem value="case">Case</SelectItem>
                            <SelectItem value="box">Box</SelectItem>
                            <SelectItem value="dozen">Dozen</SelectItem>
                            <SelectItem value="oz">Ounce</SelectItem>
                            <SelectItem value="lb">Pound</SelectItem>
                            <SelectItem value="g">Gram</SelectItem>
                            <SelectItem value="kg">Kilogram</SelectItem>
                            <SelectItem value="ml">Milliliter</SelectItem>
                            <SelectItem value="l">Liter</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {activeDrawerTab === 'planning' && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white">Order Planning</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Reorder Point</Label>
                        <Input
                          type="number"
                          value={drawerData.reorder_point || ''}
                          onChange={(e) => handleDrawerSave('reorder_point', parseInt(e.target.value))}
                          placeholder="Minimum stock level"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Target Stock</Label>
                        <Input
                          type="number"
                          value={drawerData.target_stock || ''}
                          onChange={(e) => handleDrawerSave('target_stock', parseInt(e.target.value))}
                          placeholder="Optimal stock level"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeDrawerTab === 'sales' && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white">Sales Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Selling Price</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={selectedProduct.price || ''}
                          onChange={(e) => handleDrawerSave('price', parseFloat(e.target.value))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Channel Assignment</Label>
                        <Select
                          value={drawerData.channel || 'shopify'}
                          onValueChange={(value) => handleDrawerSave('channel', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="shopify">Shopify</SelectItem>
                            <SelectItem value="amazon">Amazon</SelectItem>
                            <SelectItem value="ebay">eBay</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {activeDrawerTab === 'inventory' && selectedProduct && (
                  <div className="bg-[#0d1b2a] rounded-lg">
                    <Tabs value="inventory">
                      <ProductMasterDetail
                        productId={selectedProduct.id}
                        item={{...selectedProduct, ...drawerData}}
                        activeTab="inventory"
                        onDataChange={() => setHasUnsavedChanges(true)}
                        fullPageMode={false}
                      />
                    </Tabs>
                  </div>
                )}

                {activeDrawerTab === 'channels' && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white">Channel Management</h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Active Channels</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {['Amazon', 'Shopify', 'eBay'].map(channel => (
                            <div 
                              key={channel}
                              className="flex items-center justify-between p-3 border rounded-lg dark:border-gray-700"
                            >
                              <span className="text-sm font-medium dark:text-white">{channel}</span>
                              <Badge variant={
                                (selectedProduct.channels || []).includes(channel.toLowerCase()) 
                                  ? 'default' 
                                  : 'secondary'
                              }>
                                {(selectedProduct.channels || []).includes(channel.toLowerCase()) 
                                  ? 'Active' 
                                  : 'Inactive'}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Channel SKU Mappings</Label>
                        <div className="text-sm text-gray-500 dark:text-gray-400 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          View and manage channel-specific SKUs in the Suppliers tab or Channel SKU Manager.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeDrawerTab === 'storage' && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white">Storage & Warehouse</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Storage Condition</Label>
                        <Select
                          value={drawerData.storage_condition || 'ROOM_TEMPERATURE'}
                          onValueChange={(value) => handleDrawerSave('storage_condition', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ROOM_TEMPERATURE">Room Temperature</SelectItem>
                            <SelectItem value="COOL_DRY">Cool & Dry</SelectItem>
                            <SelectItem value="REFRIGERATED">Refrigerated</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Warehouse Location</Label>
                        <Input
                          value={drawerData.warehouse_location || ''}
                          onChange={(e) => handleDrawerSave('warehouse_location', e.target.value)}
                          placeholder="e.g., A1-B2-C3"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeDrawerTab === 'shelf-life' && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white">Shelf Life Management</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Shelf Life (Days)</Label>
                        <Input
                          type="number"
                          value={drawerData.shelf_life_days || ''}
                          onChange={(e) => handleDrawerSave('shelf_life_days', parseInt(e.target.value))}
                          placeholder="e.g., 365"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Batch Number</Label>
                        <Input
                          value={drawerData.batch_no || ''}
                          onChange={(e) => handleDrawerSave('batch_no', e.target.value)}
                          placeholder="e.g., BT2024001"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeDrawerTab === 'packaging' && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white">Packaging Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Package Weight (g)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={drawerData.package_weight || ''}
                          onChange={(e) => handleDrawerSave('package_weight', parseFloat(e.target.value))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Package Dimensions</Label>
                        <Input
                          value={drawerData.package_dimensions || ''}
                          onChange={(e) => handleDrawerSave('package_dimensions', e.target.value)}
                          placeholder="L x W x H (cm)"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeDrawerTab === 'bom' && selectedProduct && selectedProduct.product_type !== 'raw_material' && (
                  <div className="space-y-4 bg-gray-900 rounded-lg p-4">
                    <BOMManager
                      productId={selectedProduct.id}
                      productData={selectedProduct}
                      onFieldChange={(field, value) => {
                        handleDrawerSave(field, value);
                      }}
                    />
                  </div>
                )}

                {activeDrawerTab === 'pricing' && selectedProduct && (
                  <div className="space-y-4 bg-gray-900 rounded-lg p-4">
                    <PricingTab
                      key={`pricing-tab-${pricingTabKey}`}
                      ref={pricingTabSaveRef}
                      productId={selectedProduct.id}
                      productData={selectedProduct}
                      onFieldChange={(field, value) => {
                        handleDrawerSave(field, value);
                      }}
                      onNavigateToBOM={() => handleDrawerTabSwitch('bom')}
                      onDirtyChange={handlePricingDirtyChange}
                      onSaveComplete={handlePricingSaveComplete}
                    />
                  </div>
                )}

                {activeDrawerTab === 'competition' && selectedProduct && (
                  <div className="space-y-4 bg-gray-900 rounded-lg p-4">
                    <CompetitionTab
                      productId={selectedProduct.id}
                      productData={{...selectedProduct, ...drawerData}}
                      onFieldChange={(field, value) => {
                        handleDrawerSave(field, value);
                      }}
                    />
                  </div>
                )}

                {activeDrawerTab === 'suppliers' && selectedProduct && (
                  <div className="space-y-4">
                    <SupplierSkuManager 
                      productId={selectedProduct.id}
                      productSku={selectedProduct.sku}
                      onMappingChange={() => {
                        setHasUnsavedChanges(true);
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* SAP-Style Full-Page Detail View */}
      {showFullPageDetail && selectedProduct && (
        <div className="fixed inset-0 bg-white dark:bg-gray-900 z-50 flex flex-col transition-colors duration-200">
          {/* Sticky Header with Breadcrumb */}
          <div className="border-b bg-white shadow-sm sticky top-0 z-10">
            <div className="px-6 py-4">
              {/* Breadcrumb Navigation */}
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                <button
                  onClick={() => setShowFullPageDetail(false)}
                  className="flex items-center gap-1 hover:text-blue-600 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Product Master Grid
                </button>
              </div>

              {/* Product Summary Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div>
                    <h1 className="text-2xl font-semibold text-gray-900">
                      {selectedProduct.sku} - {selectedProduct.name}
                    </h1>
                    <div className="flex items-center gap-4 mt-1">
                      <Badge 
                        className={`${
                          selectedProduct.status === 'ACTIVE' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {selectedProduct.status || 'ACTIVE'}
                      </Badge>
                      <span className="text-sm text-gray-600">
                        Category: {selectedProduct.category}
                      </span>
                      <span className="text-sm text-gray-600">
                        On Hand: {selectedProduct.on_hand || 0}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Save All Button */}
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => setShowFullPageDetail(false)}>
                    Cancel
                  </Button>
                  <Button 
                    className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                    onClick={handleManualSave}
                    disabled={!hasUnsavedChanges || isSaving}
                  >
                    {isSaving ? (
                      <>
                        <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save All
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Horizontal Tab Bar - Updated for BOM/Costing System */}
            <div className="px-6 border-t">
              <div className="flex space-x-0 -mb-px">
                {[
                  { id: 'general', label: 'General', icon: Package },
                  { id: 'purchasing', label: 'Purchasing', icon: Search },
                  { id: 'sales', label: 'Sales', icon: DollarSign },
                  { id: 'inventory', label: 'Inventory', icon: Archive },
                  { id: 'channels', label: 'Channels', icon: Package },
                  // BOM tab - only for finished products
                  ...(selectedProduct?.product_type !== 'raw_material' ? [
                    { id: 'bom', label: 'BOM', icon: Package }
                  ] : []),
                  { id: 'pricing', label: 'Pricing', icon: DollarSign },
                  { id: 'competition', label: 'Competition', icon: Trophy },
                  { id: 'suppliers', label: 'Suppliers', icon: Package },
                ].map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => {
                      console.log('🔘 FULL PAGE TAB BUTTON CLICKED:', id);
                      console.log('🔘 Current state:', { activeDrawerTab, pricingTabDirty });
                      handleDrawerTabSwitch(id);
                    }}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
                      activeDrawerTab === id
                        ? 'text-blue-600 border-blue-600 bg-blue-50'
                        : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto bg-gray-50">
            <div className="max-w-6xl mx-auto p-6">
              <div className="bg-white rounded-lg shadow-sm border p-6">
                {/* Tab Content - Reuse the existing drawer content structure */}
                {activeDrawerTab === 'general' && (
                  <div className="space-y-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">General Information</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">SKU</Label>
                        <Input
                          value={selectedProduct.sku || ''}
                          onChange={(e) => handleDrawerSave('sku', e.target.value)}
                          className="font-mono"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Product Name</Label>
                        <Input
                          value={selectedProduct.name || ''}
                          onChange={(e) => handleDrawerSave('name', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Category</Label>
                        <Select
                          value={selectedProduct.category || ''}
                          onValueChange={(value) => handleDrawerSave('category', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Buckets">Buckets</SelectItem>
                            <SelectItem value="Bowls">Bowls</SelectItem>
                            <SelectItem value="Cheese Chews">Cheese Chews</SelectItem>
                            <SelectItem value="Wood">Wood</SelectItem>
                            <SelectItem value="Dog Toys">Dog Toys</SelectItem>
                            <SelectItem value="Dog Food">Dog Food</SelectItem>
                            <SelectItem value="Pet Supplies">Pet Supplies</SelectItem>
                            <SelectItem value="Raw Materials">Raw Materials</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {/* Product Type - Critical for BOM/Costing System */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Product Type</Label>
                        <Select
                          value={selectedProduct.product_type || 'finished_product'}
                          onValueChange={(value) => handleDrawerSave('product_type', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="raw_material">Raw Material</SelectItem>
                            <SelectItem value="finished_product">Finished Product</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Raw materials have landed cost. Finished products use BOM for cost calculation.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status</Label>
                        <Select
                          value={selectedProduct.status || 'ACTIVE'}
                          onValueChange={(value) => handleDrawerSave('status', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ACTIVE">Active</SelectItem>
                            <SelectItem value="ARCHIVED">Archived</SelectItem>
                            <SelectItem value="DRAFT">Draft</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Brand</Label>
                        <Input
                          value={drawerData.brand || selectedProduct.brand || ''}
                          onChange={(e) => handleDrawerSave('brand', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">UPC Code</Label>
                        <Input
                          value={drawerData.upc || selectedProduct.upc || ''}
                          onChange={(e) => handleDrawerSave('upc', e.target.value)}
                          className="font-mono"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Description</Label>
                      <textarea
                        value={drawerData.description || selectedProduct.description || ''}
                        onChange={(e) => {
                          setDrawerData(prev => ({ ...prev, description: e.target.value }));
                          handleDrawerSave('description', e.target.value);
                        }}
                        className="w-full min-h-[80px] p-3 border rounded-md bg-background text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                        placeholder="Enter product description..."
                      />
                    </div>
                  </div>
                )}
                
                {/* Additional tab content would go here - Sales, Purchasing, etc. */}
                {activeDrawerTab === 'sales' && (
                  <div className="space-y-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Sales Information</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Selling Price</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={selectedProduct.price || ''}
                          onChange={(e) => handleDrawerSave('price', parseFloat(e.target.value))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Channel Assignment</Label>
                        <Select
                          value={drawerData.channel || 'shopify'}
                          onValueChange={(value) => handleDrawerSave('channel', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="shopify">Shopify</SelectItem>
                            <SelectItem value="amazon">Amazon</SelectItem>
                            <SelectItem value="ebay">eBay</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Competition Tab Content */}
                {activeDrawerTab === 'competition' && selectedProduct && (
                  <div className="space-y-6 bg-gray-900 rounded-lg p-6">
                    <CompetitionTab
                      productId={selectedProduct.id}
                      productData={{...selectedProduct, ...drawerData}}
                      onFieldChange={(field, value) => {
                        handleDrawerSave(field, value);
                      }}
                    />
                  </div>
                )}

                {/* BOM Tab Content - Full Page View */}
                {activeDrawerTab === 'bom' && selectedProduct && selectedProduct.product_type !== 'raw_material' && (
                  <div className="space-y-6 bg-gray-900 rounded-lg p-6">
                    <BOMManager
                      productId={selectedProduct.id}
                      productData={selectedProduct}
                      onFieldChange={(field, value) => {
                        handleDrawerSave(field, value);
                      }}
                    />
                  </div>
                )}

                {/* Placeholder for other tabs */}
                {!['general', 'sales', 'competition', 'bom'].includes(activeDrawerTab) && (
                  <div className="space-y-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {activeDrawerTab.charAt(0).toUpperCase() + activeDrawerTab.slice(1)} Information
                    </h2>
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      <Package className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                      <p className="text-lg">Tab content under development</p>
                      <p className="text-sm">This tab will contain detailed {activeDrawerTab} information and settings.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Settings Modal */}
      <ViewSettingsModal
        open={showViewSettings}
        onClose={() => setShowViewSettings(false)}
        currentVariant={currentVariant}
        columnSizing={columnSizing} // Pass current column sizing for persistence
        onApplyVariant={(variant) => {
          // Apply variant including column sizing
          applyVariant(variant);
          if (variant.columnSizing) {
            setColumnSizing(variant.columnSizing);
          }
          console.log('Applied variant from modal:', variant);
        }}
        onSaveVariant={(variant) => {
          // Include current column sizing in the saved variant
          const variantWithSizing = {
            ...variant,
            columnSizing: columnSizing
          };
          saveVariant(variantWithSizing);
          console.log('Saved new variant with sizing:', variantWithSizing);
        }}
        userRoles={currentUserRoles}
        onRoleChange={switchRole}
        onGetProductCount={() => data.length}
      />
      
      {/* Column Customization Panel (Sheet) */}
      <Sheet open={showColumnPanel} onOpenChange={setShowColumnPanel}>
        <SheetContent side="right" className="w-[320px] bg-gray-900 border-gray-700 text-white overflow-y-auto">
          <SheetHeader className="pb-4 border-b border-gray-700">
            <SheetTitle className="text-white flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Customize Columns
              </span>
            </SheetTitle>
          </SheetHeader>
          
          <div className="py-4 space-y-4">
            {/* Quick Reset */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">
                {(customColumns || currentVariant?.columns || []).length} columns selected
              </span>
              <button
                onClick={() => {
                  setCustomColumns(null);
                  const allPreset = presetVariants.find(v => v.name === 'All');
                  if (allPreset) applyVariant(allPreset);
                }}
                className="text-xs text-blue-400 hover:text-blue-300"
              >
                Reset to Default
              </button>
            </div>
            
            {/* Column Checkboxes by Section */}
            {Object.entries(
              PRODUCT_COLUMN_CATALOG.reduce((acc, col) => {
                const section = col.section || 'Other';
                if (!acc[section]) acc[section] = [];
                acc[section].push(col);
                return acc;
              }, {})
            ).map(([section, columns]) => (
              <div key={section} className="space-y-2">
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                  {section}
                </div>
                <div className="space-y-1">
                  {columns.map(col => {
                    const activeColumns = customColumns || currentVariant?.columns || [];
                    const isChecked = activeColumns.includes(col.id);
                    
                    return (
                      <label
                        key={col.id}
                        className="flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-800 cursor-pointer"
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={(checked) => {
                            const currentCols = customColumns || currentVariant?.columns || [];
                            let newColumns;
                            if (checked) {
                              newColumns = [...currentCols, col.id];
                            } else {
                              newColumns = currentCols.filter(c => c !== col.id);
                            }
                            setCustomColumns(newColumns);
                            
                            // Apply the custom columns immediately
                            const customVariant = {
                              ...currentVariant,
                              id: 'custom',
                              name: 'Custom',
                              columns: newColumns,
                              config_json: {
                                ...currentVariant?.config_json,
                                columns: newColumns.map(colId => ({ id: colId, visible: true }))
                              }
                            };
                            applyVariant(customVariant);
                          }}
                          className="border-gray-600 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                        />
                        <span className="text-sm text-gray-300">{col.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
            
            {/* Save as Default Button */}
            <div className="pt-4 border-t border-gray-700">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const columnsToSave = customColumns || currentVariant?.columns || [];
                  localStorage.setItem('zuperbooks_custom_columns', JSON.stringify(columnsToSave));
                  toast.success('Column preferences saved');
                }}
                className="w-full text-xs border-gray-600 text-gray-300 hover:bg-gray-800"
              >
                <Save className="w-3 h-3 mr-2" />
                Save as Default
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
      
      {/* Phase 2: Enhanced Tooltip with improved styling */}
      {hoveredCell && (
        <div
          className="fixed z-[100] px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-2xl pointer-events-none transition-all duration-200 max-w-sm break-words border border-gray-700"
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform: 'translateX(-50%) translateY(-100%)',
            opacity: hoveredCell ? 1 : 0,
            fontSize: hoveredCell.isLongContent ? '12px' : '14px'
          }}
        >
          <div className="font-medium mb-1 text-gray-300 text-xs uppercase tracking-wide">
            {hoveredCell.columnId}
          </div>
          <div className="text-white">
            {hoveredCell.value}
          </div>
        </div>
      )}
      
      {/* Phase 3: Full-Page Product Detail Overlay - Proper z-index management */}
      {showFullPageDetail && (
        <ProductDetailPage
          open={showFullPageDetail}
          onClose={() => {
            setShowFullPageDetail(false);
            setFullPageProductId(null);
            setInitialDetailTab('general'); // ✅ Reset to general tab on close
          }}
          productId={fullPageProductId}
          initialTab={initialDetailTab}
        />
      )}

      {/* Phase 2: Task 2 - Recalculate Pricing Modal */}
      <RecalculatePricingModal
        isOpen={showRecalculateModal}
        onClose={() => setShowRecalculateModal(false)}
        onConfirm={handleRecalculateConfirm}
        {...getRecalculateCounts()}
        isLoading={isRecalculating}
        userRoles={currentUserRoles}
      />

      {/* Phase 2: Task 2 - Progress Overlay */}
      <ProgressOverlay
        isVisible={isRecalculating}
        message={recalculateProgress}
        count={getRecalculateCounts().selectedCount || getRecalculateCounts().visibleCount}
      />

      {/* Phase D: Pricing Drawer */}
      <PricingDrawer
        isOpen={isPricingDrawerOpen}
        onClose={handleClosePricingDrawer}
        productData={selectedProductForPricing}
        onSave={handlePricingDrawerSave}
      />

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <BulkProductUpload
          onClose={() => setShowBulkUpload(false)}
          onUploadComplete={handleBulkUploadComplete}
        />
      )}

      {/* Product Wizard Modal */}
      <ProductCreateWizard
        open={showProductWizard}
        onClose={() => setShowProductWizard(false)}
        onSuccess={handleProductWizardComplete}
        hideButton={true}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Delete {getSelectedProducts().length} product{getSelectedProducts().length > 1 ? 's' : ''}?
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            
            <div className="space-y-2 mb-6 max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800">
              {getSelectedProducts().map(product => (
                <div key={product.id} className="flex items-center gap-2 text-sm">
                  <Trash2 className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <span className="text-gray-900 dark:text-gray-100 font-medium">
                    {product.sku}
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">
                    - {product.name}
                  </span>
                </div>
              ))}
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-600 dark:bg-red-500 hover:bg-red-700 dark:hover:bg-red-600 text-white transition-colors flex items-center gap-2"
                onClick={handleBulkDelete}
              >
                <Trash2 className="w-4 h-4" />
                Delete Permanently
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved Changes Modal for Pricing Tab */}
      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        onClose={() => setShowUnsavedModal(false)}
        onDiscard={handleDiscardChanges}
        onSave={handleStayAndSave}
        title="Unsaved Changes"
        message="You have unsaved changes in the Pricing tab. What would you like to do?"
        saving={isSaving}
      />

      {/* Phase 2: Toast notifications for pricing updates */}
      <Toaster 
        position="top-right" 
        richColors 
        closeButton 
        duration={3000}
      />
    </div>
  );
};

// Enhanced EditableCell component with debounced autosave
const EnhancedEditableCell = ({ initialValue, onSave, type = 'text', options = [], rowId, columnId }) => {
  const [value, setValue] = useState(initialValue || '');
  const [isEditing, setIsEditing] = useState(false);
  const [saveStatus, setSaveStatus] = useState(''); // 'saving', 'saved', 'error'
  const [debounceTimer, setDebounceTimer] = useState(null);

  const debouncedSave = useCallback((newValue) => {
    // Clear existing timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Set saving status
    setSaveStatus('saving');

    // Set new timer for debounced save
    const timer = setTimeout(async () => {
      try {
        await onSave(newValue);
        setSaveStatus('saved');
        // Show success checkmark for exactly 1 second
        setTimeout(() => setSaveStatus(''), 1000);
      } catch (error) {
        setSaveStatus('error');
        // Keep error visible for 3 seconds with tooltip
        setTimeout(() => setSaveStatus(''), 3000);
      }
    }, 300); // 300ms debounce

    setDebounceTimer(timer);
  }, [debounceTimer, onSave]);

  const handleChange = (newValue) => {
    console.log('✏️ TYPING:', columnId, '=', newValue); // Should appear on every keystroke
    setValue(newValue);
    console.log('💾 CALLING SAVE?', 'NO'); // Should always say NO
    // ❌ REMOVED AUTO-SAVE: debouncedSave(newValue);
    // Save only happens when user presses Enter or clicks out of field
  };

  const handleSaveAndExit = async () => {
    console.log('💾 SAVING NOW (user finished editing)'); // Should only appear when user presses Enter or clicks out
    try {
      setSaveStatus('saving');
      await onSave(value);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(''), 1000);
    } catch (error) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveAndExit();
      setIsEditing(false);
      e.target.blur();
    } else if (e.key === 'Escape') {
      setValue(initialValue);
      setIsEditing(false);
      e.target.blur();
    }
  };

  // Update value when initialValue changes
  useEffect(() => {
    setValue(initialValue || '');
  }, [initialValue]);

  if (isEditing) {
    if (type === 'select' && options.length > 0) {
      return (
        <div className="relative">
          <Select
            value={value}
            onValueChange={async (newValue) => {
              console.log('✏️ DROPDOWN SELECTED:', columnId, '=', newValue);
              setValue(newValue);
              console.log('💾 SAVING NOW (user selected dropdown option)');
              try {
                setSaveStatus('saving');
                await onSave(newValue);
                setSaveStatus('saved');
                setTimeout(() => setSaveStatus(''), 1000);
              } catch (error) {
                setSaveStatus('error');
                setTimeout(() => setSaveStatus(''), 3000);
              }
              setIsEditing(false);
            }}
            open={true}
            onOpenChange={(open) => !open && setIsEditing(false)}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map(option => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {saveStatus && (
            <div className="absolute -right-6 top-1">
              {saveStatus === 'saving' && <Clock className="w-3 h-3 text-blue-500 animate-spin" />}
              {saveStatus === 'saved' && <CheckCircle className="w-3 h-3 text-green-500" />}
              {saveStatus === 'error' && <AlertTriangle className="w-3 h-3 text-red-500" />}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="relative">
        <Input
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            handleSaveAndExit();
            setIsEditing(false);
          }}
          className="h-8 text-xs"
          type={type === 'currency' ? 'number' : 'text'}
          step={type === 'currency' ? '0.01' : undefined}
          autoFocus
        />
        {saveStatus && (
          <div className="absolute -right-6 top-1">
            {saveStatus === 'saving' && <Clock className="w-3 h-3 text-blue-500 animate-spin" />}
            {saveStatus === 'saved' && <CheckCircle className="w-3 h-3 text-green-500" />}
            {saveStatus === 'error' && <AlertTriangle className="w-3 h-3 text-red-500" />}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`truncate cursor-pointer hover:bg-gray-100 px-1 py-1 rounded min-h-[24px] flex items-center relative ${
        saveStatus === 'error' ? 'border border-red-400 bg-red-50' : ''
      }`}
      onClick={() => setIsEditing(true)}
      title={saveStatus === 'error' ? "Save failed. Click to retry." : "Click to edit"}
    >
      <span className="flex-1">
        {type === 'currency' && value ? `$${(Number(parseFloat(value)) || 0).toFixed(2)}` : value || '-'}
      </span>
      {saveStatus && (
        <div className={`ml-2 ${saveStatus === 'saved' ? 'animate-pulse' : ''}`}>
          {saveStatus === 'saving' && <Clock className="w-3 h-3 text-blue-500 animate-spin" />}
          {saveStatus === 'saved' && <CheckCircle className="w-3 h-3 text-green-500" />}
          {saveStatus === 'error' && (
            <div className="relative group">
              <AlertTriangle className="w-3 h-3 text-red-500" />
              <div className="absolute bottom-full right-0 mb-1 hidden group-hover:block bg-red-600 text-white text-xs p-1 rounded whitespace-nowrap">
                Save failed. Try again.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Editable Cell Component
const EditableCell = ({ value, onSave, type = 'text', options = [] }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');

  const handleSave = () => {
    onSave(editValue);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value || '');
      setIsEditing(false);
    }
  };

  if (isEditing) {
    if (type === 'select') {
      return (
        <Select
          value={editValue}
          onValueChange={(val) => {
            setEditValue(val);
            onSave(val);
            setIsEditing(false);
          }}
          open={true}
          onOpenChange={(open) => !open && setIsEditing(false)}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map(option => (
              <SelectItem key={option} value={option}>{option}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    return (
      <Input
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className="h-8 text-xs"
        type={type === 'currency' ? 'number' : 'text'}
        step={type === 'currency' ? '0.01' : undefined}
        autoFocus
      />
    );
  }

  return (
    <div
      className="truncate cursor-pointer hover:bg-gray-100 px-1 py-1 rounded"
      onClick={() => setIsEditing(true)}
    >
      {type === 'currency' && value ? `$${(Number(parseFloat(value)) || 0).toFixed(2)}` : value || '-'}
    </div>
  );
};

// Using existing EnhancedEditableCell for all editing functionality

// Status Toggle Component
const StatusToggle = ({ value, onChange }) => {
  const isActive = value === 'ACTIVE';
  
  return (
    <Badge
      className={`cursor-pointer transition-colors ${
        isActive 
          ? 'bg-green-100 text-green-800 hover:bg-green-200' 
          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
      } border-0`}
      onClick={() => onChange(isActive ? 'ARCHIVED' : 'ACTIVE')}
    >
      {isActive ? 'ACTIVE' : 'ARCHIVED'}
    </Badge>
  );
};

export default SmartGrid;